  import React, { useState, useEffect, useCallback, memo, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
// Using configured axios from utils
import { 
  Alert, 
  Avatar,
  Box, 
  Button, 
  ButtonGroup, 
  Card,
  CardActionArea,
  CardContent,
  Chip, 
  CircularProgress, 
  Collapse, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle, 
  Divider, 
  FormControl, 
  FormControlLabel, 
  Grid,
  IconButton, 
  InputAdornment, 
  InputLabel, 
  LinearProgress, 
  List,
  ListItem,
  ListItemText,
  Menu, 
  MenuItem, 
  Paper, 
  Select, 
  Switch, 
  TextField, 
  Checkbox,
  Tooltip, 
  Typography, 
  useTheme 
} from '@mui/material';
import {
  useDropzone,
} from 'react-dropzone';
import {
  DragDropContext,
  Droppable,
  Draggable
} from '@hello-pangea/dnd';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Group as GroupIcon,
  Movie as MovieIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
  NightsStay as NightsStayIcon,
  Close as CloseIcon,
  CalendarToday as CalendarTodayIcon,
  Search as SearchIcon,
  FormatListNumbered as FormatListNumberedIcon,
  WbSunny as WbSunnyIcon,
  Article as ArticleIcon,
  FileDownload as FileDownloadIcon,
  Print as PrintIcon,
  Security as SecurityIcon,
  AutoFixHigh as AutoFixHighIcon,
  Face3 as Face3Icon,
  LunchDining as LunchDiningIcon,
  DirectionsCar as DirectionsCarIcon,
  Save as SaveIcon,
  AccessTime as AccessTimeIcon,
  FilterAltOff as FilterAltOffIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon
 } from '@mui/icons-material';
 import { darken, alpha } from '@mui/material/styles';
 import axios from '../utils/axiosConfig';
 import { getHierarchyStyle, hasHierarchy } from '../utils/castStyles';
 import * as pdfjsLib from 'pdfjs-dist';
 import ThreeDScene from './ThreeDScene';
 import { getScriptContent, getProjectScripts } from '../services/scriptService';
import { printSides } from './SidesPrinter';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Get dark mode compatible colors for time of day
const getTimeOfDayColors = (theme) => {
  const isDark = theme.palette.mode === 'dark';
  
  return {
    DAY: { 
      label: 'Day', 
      icon: <MovieIcon />, 
      color: isDark ? '#FFD180' : '#FFB74D' 
    },
    NIGHT: { 
      label: 'Night', 
      icon: <SecurityIcon />, 
      color: isDark ? '#8C9EFF' : '#5C6BC0' 
    },
    MORNING: { 
      label: 'Morning', 
      icon: <MovieIcon />, 
      color: isDark ? '#FFECB3' : '#FFE082' 
    },
    EVENING: { 
      label: 'Evening', 
      icon: <SecurityIcon />, 
      color: isDark ? '#B39DDB' : '#7986CB' 
    },
    DUSK: { 
      label: 'Dusk', 
      icon: <SecurityIcon />, 
      color: isDark ? '#CE93D8' : '#9575CD' 
    },
    DAWN: { 
      label: 'Dawn', 
      icon: <MovieIcon />, 
      color: isDark ? '#FFAB91' : '#FF8A65' 
    },
  };
};

const extractTextFromPDF = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = '';

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += pageText + '\n';
    }

    return fullText;
  } catch (error) {
    console.error('Error extracting text from PDF:', error);
    throw new Error('Failed to extract text from PDF');
  }
};

const parseSceneHeader = (header) => {
  try {
    // Extract location type (INT./EXT./etc.)
    const typeMatch = header.match(/^(INT\.|EXT\.|INT\/EXT\.|I\/E\.)/i);
    const locationType = typeMatch ? typeMatch[0].toUpperCase() : '';

    // Remove the location type from the header
    let remaining = header.replace(/^(INT\.|EXT\.|INT\/EXT\.|I\/E\.)/i, '').trim();

    // Extract time of day (DAY/NIGHT/etc.)
    const timeMatch = remaining.match(/(DAY|NIGHT|MORNING|AFTERNOON|EVENING|DAWN|DUSK|CONT\.|CONTINUOUS|LATER|SAME|VARIOUS|VAR\.)$/i);
    const time = timeMatch ? timeMatch[0].toUpperCase() : 'DAY';

    // Standardize time formats
    let standardizedTime = time;
    if (time === 'CONT.' || time === 'CONTINUOUS') {
      standardizedTime = 'CONT.';
    } else if (time === 'VAR.' || time === 'VARIOUS') {
      standardizedTime = 'VAR.';
    }

    // Remove the time from the remaining text
    remaining = remaining.replace(/(DAY|NIGHT|MORNING|AFTERNOON|EVENING|DAWN|DUSK|CONT\.|CONTINUOUS|LATER|SAME|VARIOUS|VAR\.)$/i, '').trim();

    // Split the remaining text into location and area (if there's a dash)
    const locationParts = remaining.split(/\s+-\s+/);
    const location = locationParts[0].trim();
    const area = locationParts.length > 1 ? locationParts[1].trim() : '';

    return {
      location_type: locationType,
      location,
      area,
      time: standardizedTime,
    };
  } catch (err) {
    console.error('Error parsing scene header:', err, header);
    return {
      location_type: '',
      location: header,
      area: '',
      time: 'DAY',
    };
  }
};

/**
 * Gets the scene number as a comparable numeric value for sorting by original script order
 */
const getSceneNumberForSort = (scene) => {
  if (!scene) return Infinity;
  
  let sceneNum = '';
  if (scene.sceneNumber) {
    if (typeof scene.sceneNumber === 'object') {
      sceneNum = scene.sceneNumber.scriptOrder || scene.sceneNumber.standard || '';
    } else {
      sceneNum = scene.sceneNumber.toString();
    }
  } else if (scene.scene_number) {
    sceneNum = scene.scene_number;
  } else if (scene.number) {
    sceneNum = scene.number;
  }
  
  // Parse scene number - handle formats like "1", "1A", "1B", etc.
  const match = sceneNum.toString().match(/^(\d+)([A-Za-z]*)$/);
  if (match) {
    const num = parseInt(match[1], 10);
    const suffix = match[2] ? match[2].charCodeAt(0) - 64 : 0; // A=1, B=2, etc.
    return num * 100 + suffix; // e.g., "1" = 100, "1A" = 101, "2" = 200
  }
  
  return parseInt(sceneNum, 10) * 100 || Infinity;
};

/**
 * Determines the inherited time of day for scenes marked as CONTINUOUS, CONT., LATER, or similar conventions.
 *
 * This function follows industry-standard screenplay formatting:
 * - If a scene is marked CONTINUOUS/CONT./LATER/SAME/IMMEDIATELY FOLLOWING, it inherits the time of day 
 *   from the PREVIOUS SCENE IN ORIGINAL SCRIPT ORDER (by scene number), NOT the current stripboard order.
 * - This ensures Scene 2 (CONT.) always inherits from Scene 1 regardless of where it's moved in the stripboard.
 *
 * @param {Object} scene - The current scene object (should have at least 'id' and 'time' fields)
 * @param {Array<Object>} allScenes - Array of all scene objects (in any order)
 * @returns {string} - The inherited or original time of day (e.g., 'DAY', 'NIGHT', etc.), or 'N/A' if none found
 */
const getInheritedTime = (scene, allScenes) => {
  if (!scene || !allScenes || !Array.isArray(allScenes)) return scene?.time || 'N/A';

  // Normalize the scene's time for comparison
  const time = scene.time ? scene.time.toUpperCase().trim() : '';

  // List of conventions that indicate time inheritance
  const inheritanceMarkers = [
    'CONTINUOUS', 'CONT.', 'LATER', 'SAME', 'IMMEDIATELY FOLLOWING', 'IMMEDIATELY', 'FOLLOWING'
  ];

  // If the scene's time is not an inheritance marker, return it as is
  const isInheritance = inheritanceMarkers.some(marker => time === marker || time.includes(marker));
  if (!isInheritance) {
    return scene.time || 'N/A';
  }

  // Sort all scenes by ORIGINAL SCRIPT ORDER (scene number), not current stripboard order
  const scenesByScriptOrder = [...allScenes].sort((a, b) => {
    return getSceneNumberForSort(a) - getSceneNumberForSort(b);
  });

  // Find the index of the current scene in script order
  const sceneIndex = scenesByScriptOrder.findIndex(s => s.id === scene.id);

  // Look backwards through scenes in SCRIPT ORDER to find the inherited time
  if (sceneIndex > 0) {
    for (let i = sceneIndex - 1; i >= 0; i--) {
      const prevTimeRaw = scenesByScriptOrder[i].time;
      const prevTime = prevTimeRaw ? prevTimeRaw.toUpperCase().trim() : '';
      const prevIsInheritance = inheritanceMarkers.some(marker => prevTime === marker || prevTime.includes(marker));
      if (prevTime && !prevIsInheritance) {
        // Found a definitive time of day (e.g., DAY, NIGHT, DAWN, DUSK, etc.)
        return scenesByScriptOrder[i].time;
      }
    }
  }

  // Edge case: if no previous definitive time is found, fallback to the current scene's time or 'N/A'
  return scene.time || 'N/A';
};

/**
 * Returns a professional color for scene strips based on scene properties
 * Uses a subtle, industry-appropriate color palette with very low opacity
 * to maintain readability while providing visual differentiation
 * 
 * For CONTINUOUS, LATER, and similar scenes, inherits the time of day from previous scenes using getInheritedTime()
 * Handles edge cases and screenplay conventions as per industry standards
 */
const getStripColor = (scene, theme, allScenes) => {
  const isDark = theme?.palette?.mode === 'dark';
  
  // Get location type (INT/EXT)
  const locationType = scene.location_type?.toUpperCase() || '';
  const isInterior = locationType.includes('INT') || locationType === 'I';
  const isExterior = locationType.includes('EXT') || locationType === 'E';
  
  // Get time of day - use getInheritedTime to resolve CONT./CONTINUOUS based on script order
  let time = scene.time ? scene.time.toUpperCase() : '';
  
  // Handle CONTINUOUS and LATER scenes by inheriting the time of day from previous scenes in SCRIPT ORDER
  if (time.includes('CONT') || time === 'CONT.' || time.includes('LATER') || time === 'LATER') {
    // Use getInheritedTime which resolves based on original script order (scene numbers)
    const inheritedTime = getInheritedTime(scene, allScenes);
    if (inheritedTime && inheritedTime !== 'N/A') {
      time = inheritedTime.toUpperCase();
    }
  }
  
  // Special case for montages - subtle purple
  if (scene.location_type === 'MONTAGE' || time.includes('VARIOUS') || time === 'VAR.') {
    return isDark ? 'rgba(103, 58, 183, 0.07)' : 'rgba(103, 58, 183, 0.04)';
  }
  
  // Interior scenes
  if (isInterior && !isExterior) {
    if (time.includes('DAY')) {
      // White for INT. DAY
      return isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(243, 243, 243, 0.22)';
    } else if (time.includes('NIGHT')) {
      // Blue for INT. NIGHT
      return isDark ? 'rgba(33, 150, 243, 0.18)' : 'rgba(30, 136, 229, 0.18)';
    } else if (time.includes('MORNING')) {
      return isDark ? 'rgba(0, 188, 212, 0.12)' : 'rgba(0, 172, 193, 0.16)';
    } else if (time.includes('EVENING')) {
      return isDark ? 'rgba(96, 125, 139, 0.12)' : 'rgba(84, 110, 122, 0.16)';
    }
  }
  
  // Exterior scenes
  if (isExterior && !isInterior) {
    if (time.includes('DAY')) {
      // Yellow for EXT. DAY
      return isDark ? 'rgba(255, 235, 59, 0.2)' : 'rgba(255, 215, 64, 0.22)';
    } else if (time.includes('NIGHT')) {
      // Purple for EXT. NIGHT
      return isDark ? 'rgba(103, 58, 183, 0.18)' : 'rgba(94, 53, 177, 0.2)';
    } else if (time.includes('DAWN') || time.includes('SUNRISE')) {
      return isDark ? 'rgba(255, 152, 0, 0.12)' : 'rgba(255, 143, 0, 0.18)';
    } else if (time.includes('DUSK') || time.includes('SUNSET')) {
      return isDark ? 'rgba(244, 67, 54, 0.12)' : 'rgba(229, 57, 53, 0.18)';
    } else if (time.includes('MORNING')) {
      return isDark ? 'rgba(139, 195, 74, 0.12)' : 'rgba(124, 179, 66, 0.18)';
    }
  }
  
  // Interior/Exterior scenes - blend of tones
  if (locationType.includes('I/E') || locationType.includes('INT/EXT')) {
    if (time.includes('DAY')) {
      return isDark ? 'rgba(0, 150, 136, 0.12)' : 'rgba(0, 137, 123, 0.18)';
    } else if (time.includes('NIGHT')) {
      return isDark ? 'rgba(103, 58, 183, 0.12)' : 'rgba(94, 53, 177, 0.16)';
    }
  }
  
  // Special cases
  if (time.includes('CONT') || time === 'CONT.') {
    return isDark ? 'rgba(158, 158, 158, 0.12)' : 'rgba(189, 189, 189, 0.16)';
  }
  
  // Default fallback - very subtle grey
  return isDark ? 'rgba(97, 97, 97, 0.12)' : 'rgba(117, 117, 117, 0.14)';
};

/**
 * Gets the inherited time for CONTINUOUS and LATER scenes
 * For scenes marked as CONTINUOUS or LATER, this function looks back through previous scenes
 * to find the first non-CONTINUOUS/LATER scene and inherits its time of day
 */
/**
 * Normalizes character names to prevent duplicates from different capitalizations or formats
 * @param {string} name - The character name to normalize
 * @param {Set} existingNames - Set of already found character names 
 * @param {boolean} strictMode - Whether to apply more strict filtering (for action lines)
 * @returns {string|null} - Normalized name or null if it should be skipped
 */
// Get custom exclusion list from localStorage
const getCustomExcludedWords = () => {
  try {
    const excluded = localStorage.getItem('customExcludedCharacterWords');
    return excluded ? JSON.parse(excluded) : [];
  } catch (err) {
    console.error('Error reading custom excluded words:', err);
    return [];
  }
};

// Add a word to the custom exclusion list
const addToCustomExcludedWords = (word) => {
  try {
    const currentExcluded = getCustomExcludedWords();
    if (!currentExcluded.includes(word.toUpperCase())) {
      const newExcluded = [...currentExcluded, word.toUpperCase()];
      localStorage.setItem('customExcludedCharacterWords', JSON.stringify(newExcluded));
      console.log(`Added "${word}" to custom excluded words list`);
      return true;
    }
    return false;
  } catch (err) {
    console.error('Error adding to custom excluded words:', err);
    return false;
  }
};

// Remove a word from the custom exclusion list
const removeFromCustomExcludedWords = (word) => {
  try {
    const currentExcluded = getCustomExcludedWords();
    const newExcluded = currentExcluded.filter(w => w !== word.toUpperCase());
    localStorage.setItem('customExcludedCharacterWords', JSON.stringify(newExcluded));
    console.log(`Removed "${word}" from custom excluded words list`);
    return true;
  } catch (err) {
    console.error('Error removing from custom excluded words:', err);
    return false;
  }
};

const normalizeCharacterName = (name, existingNames, strictMode = false) => {
  // Check custom exclusion list first
  const customExcluded = getCustomExcludedWords();
  if (customExcluded.includes(name.toUpperCase())) {
    return null;
  }
  if (!name || name.length < 2) return null;
  
  // Skip common words, abbreviations, and film terminology
  const commonWords = [
    // Common words
    'THE', 'AND', 'BUT', 'FOR', 'NOT', 'WITH', 'THAT', 'THIS', 'THEN', 'WHEN', 'WHERE', 
    'THEY', 'THERE', 'THESE', 'THOSE', 'THEIR', 'THEM', 'WHO', 'WHAT', 'WHY', 'HOW', 'WHICH',
    'FROM', 'INTO', 'ONTO', 'UPON', 'ABOUT', 'AFTER', 'BEFORE', 'DURING', 'UNTIL', 'WHILE',
    'JUST', 'VERY', 'SOME', 'ANY', 'ALL', 'MOST', 'MORE', 'LESS', 'MANY', 'MUCH', 'SUCH',
    'BOTH', 'EACH', 'EVERY', 'EITHER', 'NEITHER', 'OTHER', 'ANOTHER', 'SAME', 'TAKE',
    'COME', 'GOES', 'WENT', 'GONE', 'MAKE', 'MADE', 'LOOK', 'LOOKS', 'TURN', 'TURNS', 'LEAVE', 'LEAVES',
    'PULL', 'PUSH', 'OPEN', 'CLOSE', 'START', 'STOP', 'MOVE', 'WAIT', 'DOWN', 'BACK',
    'NEXT', 'FIRST', 'LAST', 'FOLLOW', 'LEAD', 'READ', 'WRITE', 'GOOD', 'BETTER', 'BEST',
    'WITHOUT', 'THROUGH', 'BENEATH', 'SUDDENLY', 'LANDS', 'YOU', 'TOWARD', 'AWAY',
    'NOW', 'PILOT', 'TOUCHES', 'NOTHING', 'GIVE', 'IDEA', 'WELL', 'CAN', 'AI', 'THATS',
    'APPEARS', 'ESSENCES', 'HAS', 'GOES', 'GETS', 'EVER', 'NEVER', 'YES', 'NO', 'MAYBE',
    'TODAY', 'CHANGE', 'CHANGED', 'CHANGES', 'CHANGING', 'TOMORROW', 'YESTERDAY', 'WAR',
    
    // Common verbs and actions seen in scripts
    'RUN', 'RUNS', 'RUNNING', 'WALKED', 'WALKS', 'FALLS', 'TRIPS', 'STUMBLES', 'DRAGS',
    'TACKLES', 'DODGES', 'FIRES', 'MERGES', 'SCUTTLES', 'WAVES', 'SNAP', 'SNAPS',
    'EXAMINES', 'FROZEN', 'FRANTIC', 'RUSHES', 'DUCKS', 'DIVES', 'LEAPS', 'JUMPS',
    
    // Film/screenplay terminology
    'INT', 'EXT', 'SFX', 'VFX', 'POV', 'SHOT', 'ANGLE', 'CLOSE', 'WIDE', 'CRANE', 'DOLLY',
    'HAND', 'HEAD', 'FOOT', 'FEET', 'FACE', 'BACK', 'FRONT', 'SIDE', 'BODY', 'ROOM', 'DOOR',
    'FLOOR', 'WALL', 'CEILING', 'WINDOW', 'TABLE', 'CHAIR', 'DESK', 'BED', 'PHONE', 'LIGHT',
    'NIGHT', 'DAY', 'MORNING', 'EVENING', 'AFTERNOON', 'DUSK', 'DAWN', 'SUNSET', 'SUNRISE',
    'ENTER', 'EXIT', 'WALKS', 'SITS', 'STANDS', 'RUNS', 'JUMPS', 'CONTINUOUS', 'CONT', 'LATER',
    'WARRIOR', 'ALIEN', 'DRACONIAN', 'DRACONIS',
    
    // Common actions
    'LOOKS', 'STARES', 'GLANCES', 'SEES', 'WATCHES', 'OBSERVES', 'NOTICES', 'HEARS',
    'TURNS', 'MOVES', 'WALKS', 'ENTERS', 'EXITS', 'LEAVES', 'PAUSES', 'STOPS',
    'CONTINUES', 'BEGINS', 'STARTS', 'FINISHES', 'ENDS', 'OPENS', 'CLOSES',
    'MAYHEM', 'BELOW', 'ABOVE', 'BEHIND', 'ALONGSIDE', 'INSIDE', 'OUTSIDE',
    
    // Additional exclusions
    'COMMANDER', 'LET', 'GET', 'ALMOST', 'GOT', 'COPY', 'HANG', 'HELL', 'THANKS',
    'SHIT', 'HUH', 'ARE', 'AFRAID', 'WELCOME', 'INSTEAD', 'INSISTING', 'HELLO', 'SET',
    'WOULD', 'CLEAN', 'PUT', 'WALL', 'FLOOR', 'CEILING', 'WINDOW', 'TABLE', 'CHAIR', 'DESK', 'BED', 
    'PHONE', 'LIGHT', 'PLAN', 'SUBMIT', 'LEAN',
    
    // Common verbs
    'SPEAK', 'SPEAKS', 'SPEAKING', 'SPOKE', 'SPOKEN', 'SAY', 'SAYS', 'SAYING', 'SAID',
    'TELL', 'TELLS', 'TELLING', 'TOLD', 'ASK', 'ASKS', 'ASKING', 'ASKED', 'ANSWER',
    'ANSWERS', 'ANSWERING', 'ANSWERED', 'CALL', 'CALLS', 'CALLING', 'CALLED',
    'THINK', 'THINKS', 'THINKING', 'THOUGHT', 'KNOW', 'KNOWS', 'KNOWING', 'KNEW',
    'WANT', 'WANTS', 'WANTING', 'WANTED', 'NEED', 'NEEDS', 'NEEDING', 'NEEDED',
    'FEEL', 'FEELS', 'FEELING', 'FELT', 'FIND', 'FINDS', 'FINDING', 'FOUND',
    'SHOW', 'SHOWS', 'SHOWING', 'SHOWED', 'SHOWN', 'USE', 'USES', 'USING', 'USED',
    'WORK', 'WORKS', 'WORKING', 'WORKED', 'TRY', 'TRIES', 'TRYING', 'TRIED',
    
    // Common adverbs
    'QUICKLY', 'SLOWLY', 'CAREFULLY', 'SUDDENLY', 'IMMEDIATELY', 'EVENTUALLY',
    'FINALLY', 'ACTUALLY', 'REALLY', 'DEFINITELY', 'ABSOLUTELY', 'COMPLETELY',
    'TOTALLY', 'ENTIRELY', 'MOSTLY', 'PARTLY', 'SLIGHTLY', 'BARELY', 'HARDLY',
    'NEARLY', 'ALMOST', 'QUITE', 'RATHER', 'SOMEWHAT', 'SOMEHOW', 'ANYWAY',
    
    // Common adjectives
    'BIG', 'SMALL', 'LARGE', 'TINY', 'HUGE', 'ENORMOUS', 'MASSIVE', 'GIGANTIC',
    'TALL', 'SHORT', 'LONG', 'WIDE', 'NARROW', 'THICK', 'THIN', 'HEAVY', 'LIGHT',
    'HOT', 'COLD', 'WARM', 'COOL', 'BRIGHT', 'DARK', 'CLEAR', 'CLOUDY', 'FOGGY',
    'HARD', 'SOFT', 'ROUGH', 'SMOOTH', 'SHARP', 'DULL', 'STRONG', 'WEAK', 'LOUD',
    'QUIET', 'NOISY', 'SILENT', 'FAST', 'SLOW', 'QUICK', 'RAPID', 'SUDDEN', 'GRADUAL',
    'NEW', 'OLD', 'YOUNG', 'ANCIENT', 'MODERN', 'FRESH', 'STALE', 'CLEAN', 'DIRTY',
    'WET', 'DRY', 'EMPTY', 'FULL', 'OPEN', 'CLOSED', 'BUSY', 'FREE', 'HAPPY', 'SAD',
    'ANGRY', 'CALM', 'EXCITED', 'BORED', 'TIRED', 'AWAKE', 'ASLEEP', 'ALIVE', 'DEAD'
  ];
  
  // Create a lookup set for faster checking
  const commonWordsSet = new Set(commonWords.map(word => word.toUpperCase()));
  
  // Check against our comprehensive exclusion list
  if (commonWordsSet.has(name.toUpperCase())) return null;
  
  // Skip single words in strict mode that are likely not characters
  if (strictMode && !name.includes(' ')) {
    // List of common actions, descriptors, etc.
    const singleWordFilters = [
      // Common verb endings
      /ING$/, /ED$/, /LY$/, /MENT$/,
      
      // Common verbs/actions
      /^(WALK|RUN|JUMP|SIT|STAND|LOOK|TURN|MOVE|STOP|WAIT|OPEN|CLOSE)S?$/i,
      
      // Likely not a character if too short (unless verified in cast)
      /^[A-Za-z]{1,3}$/,
      
      // Articles and prepositions
      /^(A|AN|THE|TO|IN|ON|AT|BY|FOR|FROM)$/i
    ];
    
    for (const filter of singleWordFilters) {
      if (filter.test(name)) return null;
    }
  }
  
  // Format the name for consistency (Title Case for proper names)
  const formattedName = name.includes(' ') ? 
    name : // Keep multi-word names as is
    name.charAt(0).toUpperCase() + name.slice(1).toLowerCase(); // Title case single names
  
  // Check if this name is already in the set (ignoring case)
  for (const existingName of existingNames) {
    // Case-insensitive exact match - treat "NURA" and "Nura" as the same character
    if (existingName.toUpperCase() === name.toUpperCase()) {
      // Already exists (case-insensitive), return null to prevent duplicate
      return null;
    }
    // If the names are similar (one contains the other), return the longer/more complete version
    if (existingName.toUpperCase().includes(name.toUpperCase()) || 
        name.toUpperCase().includes(existingName.toUpperCase())) {
      // Return the more complete name (usually the longer one)
      return existingName.length >= name.length ? null : formattedName;
    }
  }
  
  return formattedName;
};

const BREAK_TYPES = {
  DAY_BREAK: 'DAY_BREAK',
  LUNCH: 'LUNCH',
  COMPANY_MOVE: 'COMPANY_MOVE',
  CUSTOM: 'CUSTOM'
};

const calculateShootingDays = (breaks, scenes = []) => {
  const dayBreaks = breaks.filter(b => b.type === BREAK_TYPES.DAY_BREAK);
  
  // Filter out orphaned day breaks that reference scenes beyond the actual scene count
  const validDayBreaks = dayBreaks.filter(brk => {
    // If scenes are available, validate against scene count
    if (scenes && scenes.length > 0) {
      const maxSceneIndex = scenes.length - 1;
      const isValid = brk.afterSceneIndex <= maxSceneIndex;
      if (!isValid) {
        console.log(`OneLiner: Filtering out orphaned day break with afterSceneIndex ${brk.afterSceneIndex} (max: ${maxSceneIndex})`);
      }
      return isValid;
    }
    // If no scenes available, include all day breaks (fallback)
    return true;
  });
  
  console.log(`OneLiner: Found ${dayBreaks.length} total day breaks, ${validDayBreaks.length} valid day breaks`);
  
  // Each day break represents "End of Day X"
  // The number of shooting days equals the number of day breaks
  // (since "End of Day 9" means we have 9 complete shooting days)
  // If there are no day breaks but there are scenes, we have 1 shooting day
  if (validDayBreaks.length === 0) {
    return scenes.length > 0 ? 1 : 0;
  }
  
  return validDayBreaks.length;
};

/**
 * Calculates a detailed shooting schedule with industry-standard metrics
 * 
 * @param {Array} scenes - Array of scene objects with page counts
 * @param {Array} breaks - Array of break objects (day breaks, lunch breaks, etc.)
 * @returns {Object} - Detailed shooting schedule information
 */
const calculateShootingSchedule = (scenes, breaks) => {
  // Initialize schedule data
  const schedule = {
    totalPages: 0,
    totalEighths: 0,
    shootingDays: 1,
    averagePagesPerDay: 0,
    estimatedTotalShootHours: 0,
    dayBreakdowns: [],
    dayStartTimes: [],
    estimatedCompletionDate: null,
    sceneCount: scenes.length,
    interiorScenes: 0,
    exteriorScenes: 0,
    dayScenes: 0,
    nightScenes: 0,
  };
  
  // Count scene types
  scenes.forEach(scene => {
    // Calculate total pages
    const pageCount = parseFloat(scene.pages) || 0;
    schedule.totalPages += pageCount;
    schedule.totalEighths += Math.round(pageCount * 8);
    
    // Count scene types
    const locationType = scene.location_type?.toUpperCase() || '';
    if (locationType.includes('INT') || locationType === 'I') {
      schedule.interiorScenes++;
    }
    if (locationType.includes('EXT') || locationType === 'E') {
      schedule.exteriorScenes++;
    }
    
    const time = scene.time ? scene.time.toUpperCase() : '';
    if (time.includes('DAY') || time.includes('MORNING') || time.includes('AFTERNOON')) {
      schedule.dayScenes++;
    }
    if (time.includes('NIGHT') || time.includes('EVENING')) {
      schedule.nightScenes++;
    }
  });
  
  // Calculate shooting days based on day breaks
  const dayBreaks = breaks.filter(b => b.type === BREAK_TYPES.DAY_BREAK);
  schedule.shootingDays = dayBreaks.length + 1; // Number of day breaks plus current day
  
  // Calculate average pages per day
  schedule.averagePagesPerDay = schedule.shootingDays > 0 
    ? (schedule.totalPages / schedule.shootingDays).toFixed(1) 
    : schedule.totalPages.toFixed(1);
  
  // Calculate estimated shoot hours (industry standard: ~1 hour per 1/8 page)
  schedule.estimatedTotalShootHours = Math.ceil(schedule.totalEighths / 8);
  
  // Calculate day breakdowns
  let currentDay = 1;
  let currentDayPages = 0;
  let currentDayScenes = [];
  
  // Sort scenes by their order
  const sortedScenes = [...scenes].sort((a, b) => (a.order || 0) - (b.order || 0));
  
  // Create day breakdowns
  sortedScenes.forEach((scene, index) => {
    const pageCount = parseFloat(scene.pages) || 0;
    currentDayPages += pageCount;
    currentDayScenes.push(scene.id);
    
    // Check if there's a day break after this scene
    const sceneIndex = index;
    const hasBreakAfter = dayBreaks.some(b => b.afterSceneIndex === sceneIndex);
    
    if (hasBreakAfter || index === sortedScenes.length - 1) {
      // Calculate estimated shoot time for this day
      const dayEighths = Math.round(currentDayPages * 8);
      const shootHours = Math.ceil(dayEighths / 8);
      
      // Calculate call time based on page count
      const callTime = estimateCallTime(currentDayPages, currentDay);
      
      schedule.dayBreakdowns.push({
        day: currentDay,
        pages: currentDayPages.toFixed(1),
        eighths: dayEighths,
        scenes: currentDayScenes.length,
        estimatedHours: shootHours,
        callTime: callTime,
      });
      
      schedule.dayStartTimes.push(callTime);
      
      // Reset for next day
      currentDay++;
      currentDayPages = 0;
      currentDayScenes = [];
    }
  });
  
  // Calculate estimated completion date
  const startDate = new Date();
  const completionDate = new Date(startDate);
  completionDate.setDate(startDate.getDate() + schedule.shootingDays - 1);
  schedule.estimatedCompletionDate = completionDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
  
  return schedule;
};

/**
 * Estimates call time based on page count and day number
 * Uses industry standards for call times based on shooting complexity
 */
const estimateCallTime = (pageCount, dayNumber) => {
  // Base call time: 7:00 AM for first day, 8:00 AM for subsequent days with standard page count
  const baseHour = dayNumber === 1 ? 7 : 8;
  const baseMinute = 0;
  
  // Adjust call time based on page count (earlier call for more pages)
  let callHour = baseHour;
  let callMinute = baseMinute;
  
  // For every 3 pages over 5, move call time 15 minutes earlier
  if (pageCount > 5) {
    const pagesOver = pageCount - 5;
    const earlierMinutes = Math.floor(pagesOver / 3) * 15;
    
    // Calculate new time
    let totalMinutes = callHour * 60 + callMinute - earlierMinutes;
    callHour = Math.floor(totalMinutes / 60);
    callMinute = totalMinutes % 60;
    
    // Ensure we don't go below 5:00 AM
    if (callHour < 5) {
      callHour = 5;
      callMinute = 0;
    }
  }
  
  // Format time as 12-hour with AM/PM
  const period = callHour >= 12 ? 'PM' : 'AM';
  const hour12 = callHour > 12 ? callHour - 12 : (callHour === 0 ? 12 : callHour);
  const minuteStr = callMinute.toString().padStart(2, '0');
  
  return `${hour12}:${minuteStr} ${period}`;
};

/**
 * Formats page count in industry-standard 1/8ths
 * 
 * In film production, each page is divided into 8 equal parts (approximately 1 inch each).
 * This standardized measurement is used to estimate screen time and shooting time.
 * 
 * @param {number|string} pages - The page count to format
 * @returns {string} - Formatted page count in 1/8ths (e.g., "2 4/8" or "3/8")
 */
const formatPagesInEighths = (pages) => {
  if (!pages || isNaN(parseFloat(pages))) return '-';
  
  const pagesNum = parseFloat(pages);
  const pagesInEighths = Math.round(pagesNum * 8);
  const wholePages = Math.floor(pagesInEighths / 8);
  const eighths = pagesInEighths % 8;
  
  if (eighths === 0) {
    // Just whole pages
    return wholePages === 0 ? '0' : `${wholePages}`;
  } else if (wholePages === 0) {
    // Just a fraction of a page
    return `${eighths}/8`;
  } else {
    // Whole pages plus a fraction
    return `${wholePages} ${eighths}/8`;
  }
};

const formatSceneTime = (time) => {
  if (!time) return '';
  
  // Convert to uppercase for consistent comparison
  const upperTime = time.toUpperCase();
  
  // Handle abbreviations
  if (upperTime === 'CONTINUOUS' || upperTime === 'CONT.') {
    return 'CONT.';
  } else if (upperTime === 'VARIOUS') {
    return 'VAR.';
  }
  
  return time;
};

const Break = memo(({ type, onRemove, breakIndex, id, allBreaks, onUpdate, onUpdateBreak, scenes, shootingStartDate }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [isEditing, setIsEditing] = useState(false);
  
  // State for editable fields
  const [customDate, setCustomDate] = useState('');
  const [customDayNumber, setCustomDayNumber] = useState('');
  const [customPages, setCustomPages] = useState('');
  const [customTime, setCustomTime] = useState('');
  const [customName, setCustomName] = useState('');

  const parseISODate = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const formatISODate = (dateObj) => {
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return '';
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load existing data when component mounts
  useEffect(() => {
    // Find the current break data (use _id for MongoDB documents)
    const breakData = allBreaks.find(b => b._id === id);
    
    if (breakData) {
      // Load custom values if they exist
      setCustomDate(breakData.customDate || '');
      setCustomDayNumber(breakData.customDayNumber || '');
      setCustomPages(breakData.customPages || '');
      setCustomTime(breakData.customTime || '');
      setCustomName(breakData.customName || '');
      console.log('Loaded break data:', { id, customDate: breakData.customDate });
    }
  }, [id, allBreaks]);
  
  const handleEditClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setIsEditing(false);
    
    // Reset all values to last saved values
    const breakData = allBreaks.find(b => b._id === id);
    if (breakData) {
      setCustomDate(breakData.customDate || '');
      setCustomDayNumber(breakData.customDayNumber || '');
      setCustomPages(breakData.customPages || '');
      setCustomTime(breakData.customTime || '');
      setCustomName(breakData.customName || '');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Saving break with customDate:', customDate);
    const breakData = allBreaks.find(b => b._id === id);
    const previousCustomDate = breakData?.customDate || '';
    
    // Save all editable values
    const updateData = { 
      customDate,
      customDayNumber,
      customPages,
      customTime,
      customName
    };
    console.log('Update data being sent:', updateData);
    
    await onUpdate(updateData);
    
    // If this is a day break and the date was changed, cascade to ALL subsequent breaks
    if (type === BREAK_TYPES.DAY_BREAK && customDate && onUpdateBreak) {
      const dayBreaks = allBreaks
        .filter(b => b.type === BREAK_TYPES.DAY_BREAK)
        .sort((a, b) => a.afterSceneIndex - b.afterSceneIndex);
      
      const currentIndex = dayBreaks.findIndex(b => b._id === id);
      
      if (currentIndex >= 0 && currentIndex < dayBreaks.length - 1) {
        const baseDate = parseISODate(customDate);
        const previousDate = previousCustomDate ? parseISODate(previousCustomDate) : null;
        const MS_PER_DAY = 24 * 60 * 60 * 1000;
        const dateShift = baseDate && previousDate
          ? Math.round((baseDate.getTime() - previousDate.getTime()) / MS_PER_DAY)
          : null;
        let sequentialBaseDate = baseDate ? new Date(baseDate) : null;

        for (let i = currentIndex + 1; i < dayBreaks.length; i++) {
          const nextBreak = dayBreaks[i];
          let targetDate = null;

          if (dateShift !== null && nextBreak.customDate) {
            const originalNextDate = parseISODate(nextBreak.customDate);
            if (originalNextDate) {
              targetDate = new Date(originalNextDate);
              targetDate.setDate(targetDate.getDate() + dateShift);
            }
          }

          if (!targetDate && sequentialBaseDate) {
            targetDate = new Date(sequentialBaseDate);
            targetDate.setDate(targetDate.getDate() + 1);
          }

          if (!targetDate) continue;

          sequentialBaseDate = new Date(targetDate);
          const newDateStr = formatISODate(targetDate);
          console.log(`Cascading date to Day ${i + 1}: ${newDateStr}`);
          await onUpdateBreak(nextBreak._id, { customDate: newDateStr });
        }
      }
    }
    
    setIsEditing(false);
  };

  const getBreakStyle = () => {
    // Define professional colors for different break types
    let bgColor, borderColor;
    
    if (type === BREAK_TYPES.DAY_BREAK) {
      // Day break - dark blue/black professional look
      bgColor = isDark ? '#1A2027' : '#263238';
      borderColor = theme.palette.primary.main;
    } else if (type === BREAK_TYPES.LUNCH) {
      // Lunch break - warm amber tone distinct from EXT/DUSK
      bgColor = isDark ? 'rgba(255, 183, 77, 0.2)' : '#4E342E';
      borderColor = theme.palette.warning.main;
    } else if (type === BREAK_TYPES.CUSTOM) {
      // Custom break - purple/violet tone
      bgColor = isDark ? '#4A148C' : '#6A1B9A';
      borderColor = theme.palette.secondary.main;
    } else {
      // Company move - cool blue tone
      bgColor = isDark ? '#0D47A1' : '#1565C0';
      borderColor = theme.palette.info.main;
    }
    
    const baseStyle = {
      backgroundColor: bgColor,
      color: 'white',
      height: 'auto',
      minHeight: '60px',
      position: 'relative',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      borderLeft: `5px solid ${borderColor}`,
      borderRadius: '3px',
    };

    return baseStyle;
  };

  const getBreakIcon = () => {
    const iconStyle = { 
      color: 'white', 
      fontSize: '1.5rem',
      opacity: 0.9,
      marginRight: '8px'
    };
    switch (type) {
      case BREAK_TYPES.DAY_BREAK:
        return <NightsStayIcon style={iconStyle} />;
      case BREAK_TYPES.LUNCH:
        return <LunchDiningIcon style={iconStyle} />;
      case BREAK_TYPES.COMPANY_MOVE:
        return <DirectionsCarIcon style={iconStyle} />;
      case BREAK_TYPES.CUSTOM:
        return <EditIcon style={iconStyle} />;
      default:
        return null;
    }
  };

  // Calculate the total pages for scenes above the break
  const calculatePageCount = () => {
    if (type !== BREAK_TYPES.DAY_BREAK || !allBreaks || !scenes || scenes.length === 0) return 0;
    
    // Find the index of this break in the allBreaks array
    const breakIndex = allBreaks.findIndex(b => b._id === id);
    if (breakIndex === -1) return 0;
    
    // Get all day breaks before this one
    const dayBreaksBeforeThis = allBreaks
      .filter(b => b.type === BREAK_TYPES.DAY_BREAK)
      .slice(0, breakIndex);
    
    // Get the scene index after which this break occurs
    const afterSceneIndex = allBreaks.find(b => b._id === id)?.afterSceneIndex;
    if (afterSceneIndex === undefined) return 0;
    
    // If this is the first day break, count pages from the beginning to this break
    if (dayBreaksBeforeThis.length === 0) {
      // Sum pages for all scenes from index 0 to afterSceneIndex
      return scenes
        .filter((_, index) => index <= afterSceneIndex)
        .reduce((sum, scene) => sum + (scene.pages || 0), 0);
    } else {
      // Get the scene index of the previous day break
      const prevDayBreakSceneIndex = dayBreaksBeforeThis[dayBreaksBeforeThis.length - 1].afterSceneIndex;
      
      // Sum pages for scenes between the previous day break and this one
      return scenes
        .filter((_, index) => index > prevDayBreakSceneIndex && index <= afterSceneIndex)
        .reduce((sum, scene) => sum + (scene.pages || 0), 0);
    }
  };
  
  // Estimate call time based on page count and standard shooting day
  const estimateShootTime = (pageCount) => {
    // Standard call time for first day is 7:00 AM
    // For subsequent days, adjust based on page count
    
    // Base call time: 7:00 AM
    const baseHour = 7;
    const baseMinute = 0;
    
    // Adjust call time based on page count (earlier call for more pages)
    let callHour = baseHour;
    let callMinute = baseMinute;
    
    // For every 3 pages over 5, move call time 15 minutes earlier
    if (pageCount > 5) {
      const pagesOver = pageCount - 5;
      const earlierMinutes = Math.floor(pagesOver / 3) * 15;
      
      // Calculate new time
      let totalMinutes = callHour * 60 + callMinute - earlierMinutes;
      callHour = Math.floor(totalMinutes / 60);
      callMinute = totalMinutes % 60;
      
      // Ensure we don't go below 5:00 AM
      if (callHour < 5) {
        callHour = 5;
        callMinute = 0;
      }
    }
    
    // Format time as 12-hour with AM/PM
    const period = callHour >= 12 ? 'PM' : 'AM';
    const hour12 = callHour > 12 ? callHour - 12 : (callHour === 0 ? 12 : callHour);
    const minuteStr = callMinute.toString().padStart(2, '0');
    
    return `${hour12}:${minuteStr} ${period}`;
  };
  
  // Get the date for this shooting day based on start date
  const getShootDate = (dayNumber) => {
    // Use shootingStartDate prop if available, otherwise default to today
    const startDate = shootingStartDate ? new Date(shootingStartDate) : new Date();
    const date = new Date(startDate);
    date.setDate(date.getDate() + dayNumber - 1);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getBreakText = () => {
    switch (type) {
      case BREAK_TYPES.DAY_BREAK: {
        // Calculate default values
        const dayBreaks = allBreaks.filter(b => b.type === BREAK_TYPES.DAY_BREAK);
        const dayIndex = dayBreaks.findIndex(b => b._id === id);
        const dayNumber = dayIndex + 2;
        
        // Calculate date based on previous break's customDate if available
        let shootDate;
        if (dayIndex > 0) {
          const prevBreak = dayBreaks[dayIndex - 1];
          if (prevBreak?.customDate) {
            // Previous break has custom date - calculate from there
            const [year, month, day] = prevBreak.customDate.split('-').map(Number);
            const prevDate = new Date(year, month - 1, day);
            const nextDate = new Date(prevDate);
            nextDate.setDate(nextDate.getDate() + 1);
            shootDate = nextDate.toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric',
              year: 'numeric'
            });
          } else {
            // No custom date on previous break - use default calculation
            shootDate = getShootDate(dayNumber);
          }
        } else {
          // First break - use default calculation from start date
          shootDate = getShootDate(dayNumber);
        }
        
        const pageCount = calculatePageCount();
        const formattedPageCount = formatPagesInEighths(pageCount);
        const shootTime = estimateShootTime(pageCount);
        
        // Use custom values if available, otherwise use calculated values
        const displayDayNumber = customDayNumber || (dayNumber - 1);
        // Format custom date if it exists (it's in YYYY-MM-DD format from input)
        let displayDate = shootDate;
        if (customDate) {
          const [year, month, day] = customDate.split('-').map(Number);
          const dateObj = new Date(year, month - 1, day);
          displayDate = dateObj.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
          });
        }
        const displayPages = customPages || formattedPageCount;
        const displayTime = customTime || shootTime;
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: { xs: 1, sm: 2 } }}>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 'bold', 
              mr: 2,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              fontSize: '0.95rem'
            }}>
              End of Day {displayDayNumber}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexWrap: 'wrap',
              gap: { xs: 1.5, sm: 3 },
              '& > *': { whiteSpace: 'nowrap' }
            }}>
              <Typography variant="body2" sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '0.85rem',
                fontWeight: 500
              }}>
                <CalendarTodayIcon sx={{ fontSize: '0.9rem', mr: 0.7, opacity: 0.8 }} />
                {displayDate}
              </Typography>
              <Typography variant="body2" sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '0.85rem',
                fontWeight: 500
              }}>
                <ArticleIcon sx={{ fontSize: '0.9rem', mr: 0.7, opacity: 0.8 }} />
                {displayPages} pages
              </Typography>
              <Typography variant="body2" sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '0.85rem',
                fontWeight: 500
              }}>
                <WbSunnyIcon sx={{ fontSize: '0.9rem', mr: 0.7, opacity: 0.8 }} />
                Call: {displayTime}
              </Typography>
            </Box>
          </Box>
        );
      }
      case BREAK_TYPES.LUNCH:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 'bold', 
              display: 'flex', 
              alignItems: 'center',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              fontSize: '0.95rem'
            }}>
              {customName || 'Lunch Break'}
            </Typography>
            {customTime && (
              <Typography variant="body2" sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '0.85rem',
                fontWeight: 500,
                opacity: 0.9
              }}>
                <AccessTimeIcon sx={{ fontSize: '0.9rem', mr: 0.5, opacity: 0.8 }} />
                {customTime}
              </Typography>
            )}
          </Box>
        );
      case BREAK_TYPES.COMPANY_MOVE:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 'bold', 
              display: 'flex', 
              alignItems: 'center',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              fontSize: '0.95rem'
            }}>
              Company Move
            </Typography>
            {customTime && (
              <Typography variant="body2" sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '0.85rem',
                fontWeight: 500,
                opacity: 0.9
              }}>
                <AccessTimeIcon sx={{ fontSize: '0.9rem', mr: 0.5, opacity: 0.8 }} />
                {customTime}
              </Typography>
            )}
          </Box>
        );
      case BREAK_TYPES.CUSTOM:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 'bold', 
              display: 'flex', 
              alignItems: 'center',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              fontSize: '0.95rem'
            }}>
              {customName || 'Custom Break'}
            </Typography>
            {customTime && (
              <Typography variant="body2" sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '0.85rem',
                fontWeight: 500,
                opacity: 0.9
              }}>
                <AccessTimeIcon sx={{ fontSize: '0.9rem', mr: 0.5, opacity: 0.8 }} />
                {customTime}
              </Typography>
            )}
          </Box>
        );
      default:
        return '';
    }
  };

  return (
    <Paper 
      elevation={2}
      sx={{
        ...getBreakStyle(),
        display: 'flex',
        flexDirection: 'column',
        padding: '14px 16px',
        margin: '10px 0',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
          transform: 'translateY(-1px)'
        }
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        width: '100%'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {getBreakIcon()}
          <Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 'bold',
              color: 'white',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }}>
              {getBreakText()}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={isEditing ? "Cancel editing" : "Edit break notes"}>
            <IconButton 
              size="small" 
              onClick={isEditing ? handleClose : handleEditClick}
              sx={{ 
                color: 'white',
                bgcolor: isEditing ? 'rgba(255,255,255,0.1)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              {isEditing ? <CloseIcon fontSize="small" /> : <EditIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {!isEditing && (
            <Tooltip title="Remove break">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Remove this ${type.replace('_', ' ').toLowerCase()}?`)) {
                    onRemove();
                  }
                }} 
                sx={{ 
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,0,0,0.2)'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      
      {/* Professional Edit Dialog */}
      <Dialog
        open={isEditing}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2
        }}>
          {getBreakIcon()}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
              {type === BREAK_TYPES.DAY_BREAK ? 'Edit Day Break' : 
               type === BREAK_TYPES.LUNCH ? 'Edit Lunch Break' : 'Edit Company Move'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {type === BREAK_TYPES.DAY_BREAK ? 'Configure shooting day transition' :
               type === BREAK_TYPES.LUNCH ? 'Set lunch break details' : 'Configure company move'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          {type === BREAK_TYPES.DAY_BREAK && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Day Info Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  fontWeight: 600, 
                  color: theme.palette.primary.main,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <CalendarTodayIcon sx={{ fontSize: 18 }} />
                  Day Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TextField
                    size="small"
                    label="Day Number"
                    variant="outlined"
                    value={customDayNumber}
                    onChange={(e) => setCustomDayNumber(e.target.value)}
                    placeholder="e.g., 2"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FormatListNumberedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        </InputAdornment>
                      )
                    }}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Shoot Date"
                    variant="outlined"
                    type="date"
                    value={customDate || ''}
                    onChange={(e) => setCustomDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>
              </Box>
              
              {/* Production Details Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  fontWeight: 600, 
                  color: theme.palette.warning.main,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <ArticleIcon sx={{ fontSize: 18 }} />
                  Production Details
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TextField
                    size="small"
                    label="Total Pages"
                    variant="outlined"
                    value={customPages}
                    onChange={(e) => setCustomPages(e.target.value)}
                    placeholder="e.g., 3 2/8"
                    helperText="Pages shot this day"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ArticleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        </InputAdornment>
                      )
                    }}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Call Time"
                    variant="outlined"
                    type="time"
                    value={customTime ? customTime.replace(/\s*(AM|PM)/i, '').replace(/(\d+):(\d+)/, (m, h, min) => {
                      const hour = parseInt(h);
                      const isPM = customTime.toUpperCase().includes('PM');
                      const hour24 = isPM && hour !== 12 ? hour + 12 : (!isPM && hour === 12 ? 0 : hour);
                      return `${hour24.toString().padStart(2, '0')}:${min}`;
                    }) : ''}
                    onChange={(e) => {
                      const timeValue = e.target.value;
                      if (timeValue) {
                        const [hours, minutes] = timeValue.split(':');
                        const hour = parseInt(hours, 10);
                        const period = hour >= 12 ? 'PM' : 'AM';
                        const hour12 = hour % 12 || 12;
                        setCustomTime(`${hour12}:${minutes} ${period}`);
                      } else {
                        setCustomTime('');
                      }
                    }}
                    helperText="General crew call"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        </InputAdornment>
                      )
                    }}
                    fullWidth
                  />
                </Box>
              </Box>
            </Box>
          )}
          
          {type === BREAK_TYPES.LUNCH && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Meal break marker. You can rename this to Breakfast, Second Meal, etc.
              </Typography>
              <TextField
                size="small"
                label="Break Name"
                variant="outlined"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="e.g., Breakfast, Lunch, Second Meal"
                helperText="Custom name for this meal break"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LunchDiningIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  )
                }}
                fullWidth
              />
              <TextField
                size="small"
                label="Break Time"
                variant="outlined"
                type="time"
                value={customTime ? customTime.replace(/\s*(AM|PM)/i, '').replace(/(\d+):(\d+)/, (m, h, min) => {
                  const hour = parseInt(h);
                  const isPM = customTime.toUpperCase().includes('PM');
                  const hour24 = isPM && hour !== 12 ? hour + 12 : (!isPM && hour === 12 ? 0 : hour);
                  return `${hour24.toString().padStart(2, '0')}:${min}`;
                }) : ''}
                onChange={(e) => {
                  const timeValue = e.target.value;
                  if (timeValue) {
                    const [hours, minutes] = timeValue.split(':');
                    const hour = parseInt(hours, 10);
                    const period = hour >= 12 ? 'PM' : 'AM';
                    const hour12 = hour % 12 || 12;
                    setCustomTime(`${hour12}:${minutes} ${period}`);
                  } else {
                    setCustomTime('');
                  }
                }}
                helperText="Scheduled break time"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  )
                }}
                fullWidth
              />
            </Box>
          )}
          
          {type === BREAK_TYPES.COMPANY_MOVE && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Company move marker. This indicates a location change during the shooting day.
              </Typography>
              <TextField
                size="small"
                label="Move Time"
                variant="outlined"
                type="time"
                value={customTime ? customTime.replace(/\s*(AM|PM)/i, '').replace(/(\d+):(\d+)/, (m, h, min) => {
                  const hour = parseInt(h);
                  const isPM = customTime.toUpperCase().includes('PM');
                  const hour24 = isPM && hour !== 12 ? hour + 12 : (!isPM && hour === 12 ? 0 : hour);
                  return `${hour24.toString().padStart(2, '0')}:${min}`;
                }) : ''}
                onChange={(e) => {
                  const timeValue = e.target.value;
                  if (timeValue) {
                    const [hours, minutes] = timeValue.split(':');
                    const hour = parseInt(hours, 10);
                    const period = hour >= 12 ? 'PM' : 'AM';
                    const hour12 = hour % 12 || 12;
                    setCustomTime(`${hour12}:${minutes} ${period}`);
                  } else {
                    setCustomTime('');
                  }
                }}
                helperText="Estimated move time"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DirectionsCarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  )
                }}
                fullWidth
              />
            </Box>
          )}
          
          {type === BREAK_TYPES.CUSTOM && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Custom break marker. Add any type of break or note to your schedule.
              </Typography>
              <TextField
                size="small"
                label="Break Name"
                variant="outlined"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="e.g., Hair/Makeup, Wardrobe Change, Weather Hold"
                helperText="Name for this custom break"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EditIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  )
                }}
                fullWidth
              />
              <TextField
                size="small"
                label="Time (Optional)"
                variant="outlined"
                type="time"
                value={customTime ? customTime.replace(/\s*(AM|PM)/i, '').replace(/(\d+):(\d+)/, (m, h, min) => {
                  const hour = parseInt(h);
                  const isPM = customTime.toUpperCase().includes('PM');
                  const hour24 = isPM && hour !== 12 ? hour + 12 : (!isPM && hour === 12 ? 0 : hour);
                  return `${hour24.toString().padStart(2, '0')}:${min}`;
                }) : ''}
                onChange={(e) => {
                  const timeValue = e.target.value;
                  if (timeValue) {
                    const [hours, minutes] = timeValue.split(':');
                    const hour = parseInt(hours, 10);
                    const period = hour >= 12 ? 'PM' : 'AM';
                    const hour12 = hour % 12 || 12;
                    setCustomTime(`${hour12}:${minutes} ${period}`);
                  } else {
                    setCustomTime('');
                  }
                }}
                helperText="Scheduled time for this break"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  )
                }}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          px: 3, 
          py: 2, 
          borderTop: `1px solid ${theme.palette.divider}`,
          gap: 1
        }}>
          <Button 
            onClick={handleClose}
            color="inherit"
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{ 
              px: 3,
              borderRadius: 2
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
});

const OneLiner = ({ projectId }) => {
  const [scenes, setScenes] = useState([]);
  const [selectedScene, setSelectedScene] = useState(null);
  const [breaks, setBreaks] = useState([]);
  const [breaksBySceneIndex, setBreaksBySceneIndex] = useState({});
  const [scriptRawText, setScriptRawText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [stats, setStats] = useState({ totalPages: 0, shootingDays: 1 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scripts, setScripts] = useState([]);
  const [selectedScript, setSelectedScript] = useState(null);
  const [loadingScripts, setLoadingScripts] = useState(false);
  const [selectedSceneIndex, setSelectedSceneIndex] = useState(null);
  const [castMembers, setCastMembers] = useState([]);
  const [loadingCast, setLoadingCast] = useState(false);
  const [excludedWords, setExcludedWords] = useState([]);
  const [excludedWordsDialogOpen, setExcludedWordsDialogOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // Sides print state
  const [sidesDialogOpen, setSidesDialogOpen] = useState(false);
  const [sidesSelectedSceneIds, setSidesSelectedSceneIds] = useState([]);
  const [sidesLayout, setSidesLayout] = useState('half-letter'); // 'half-letter' | 'two-up'
  const [sidesSelectedDay, setSidesSelectedDay] = useState(1);
  // Shooting start date state - defaults to today, persisted in localStorage
  const [shootingStartDate, setShootingStartDate] = useState(() => {
    const saved = localStorage.getItem(`oneliner_startDate_${projectId}`);
    return saved || new Date().toISOString().split('T')[0];
  });
  const [isStartDateLocked, setIsStartDateLocked] = useState(() => {
    const storedLock = localStorage.getItem(`oneliner_startDateLocked_${projectId}`);
    if (storedLock !== null) {
      return storedLock === 'true';
    }
    const saved = localStorage.getItem(`oneliner_startDate_${projectId}`);
    return !!saved;
  });

  useEffect(() => {
    const savedDate = localStorage.getItem(`oneliner_startDate_${projectId}`);
    if (savedDate) {
      setShootingStartDate(savedDate);
    }
    const storedLock = localStorage.getItem(`oneliner_startDateLocked_${projectId}`);
    if (storedLock !== null) {
      setIsStartDateLocked(storedLock === 'true');
    } else if (savedDate) {
      setIsStartDateLocked(true);
    }
  }, [projectId]);

  const setStartDateLockState = useCallback((locked) => {
    setIsStartDateLocked(locked);
    try {
      localStorage.setItem(`oneliner_startDateLocked_${projectId}`, locked ? 'true' : 'false');
    } catch (err) {
      console.warn('Unable to persist start date lock state', err);
    }
  }, [projectId]);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const timeOfDay = getTimeOfDayColors(theme);
  
  // Consistent text field styling
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontSize: '0.95rem',
      fontWeight: 600
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.default, 0.3)
        : alpha(theme.palette.grey[50], 0.5),
      borderRadius: 2.5,
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderWidth: 1.5
      },
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.default, 0.4)
          : alpha(theme.palette.grey[50], 0.8),
        '& fieldset': {
          borderColor: theme.palette.primary.main
        }
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.05)
          : alpha(theme.palette.primary.main, 0.02),
        '& fieldset': {
          borderWidth: 2
        }
      }
    }
  };

  // Compute day ranges from DAY_BREAKs to match stripboard
  const daySceneIdRanges = useMemo(() => {
    if (!Array.isArray(scenes) || scenes.length === 0) return [];
    const dayBreaks = (breaks || []).filter(b => b.type === BREAK_TYPES.DAY_BREAK).sort((a,b)=> (a.afterSceneIndex||0)-(b.afterSceneIndex||0));
    const indices = dayBreaks.map(b => Math.min(Math.max(0, b.afterSceneIndex), scenes.length - 1));
    const ranges = [];
    let start = 0;
    for (const idx of indices) {
      const end = Math.min(idx, scenes.length - 1);
      const ids = scenes.slice(start, end + 1).map(s => s.id).filter(Boolean);
      if (ids.length) ranges.push(ids);
      start = end + 1;
    }
    if (start <= scenes.length - 1) {
      const ids = scenes.slice(start).map(s => s.id).filter(Boolean);
      if (ids.length) ranges.push(ids);
    }
    return ranges;
  }, [breaks, scenes]);

  // Notify callsheet (and any other listener) whenever shooting days change
  // so sibling components that stay mounted can pick up the new total.
  useEffect(() => {
    const totalDays = calculateShootingDays(breaks, scenes);
    // Persist to localStorage so callsheet can read it even on first mount
    if (projectId) {
      localStorage.setItem(`oneliner_totalShootingDays_${projectId}`, String(totalDays));
    }
    window.dispatchEvent(new CustomEvent('breaksUpdated', {
      detail: { projectId, totalShootingDays: totalDays }
    }));
  }, [breaks, scenes, projectId]);

  // Build a header string similar to screenplay scene slug
  const buildSceneHeader = useCallback((scene) => {
    if (!scene) return '';
    const type = (scene.location_type || '').toUpperCase();
    const loc = scene.location || '';
    const area = scene.area ? ` - ${scene.area}` : '';
    const time = (scene.time || '').toUpperCase();
    const parts = [];
    if (type) parts.push(type.endsWith('.') ? type : `${type}.`);
    parts.push((loc + area).trim());
    if (time) parts.push(time);
    return parts.filter(Boolean).join(' ');
  }, []);

  // Open sides selection dialog (preselect all scenes)
  const openSidesDialog = useCallback(() => {
    const ranges = daySceneIdRanges;
    const day = Math.max(1, Math.min(ranges.length || 1, 1));
    setSidesSelectedDay(day);
    setSidesSelectedSceneIds((ranges[day-1] && ranges[day-1].length) ? ranges[day-1] : scenes.map(s => s.id).filter(Boolean));
    setSidesLayout('half-letter');
    setSidesDialogOpen(true);
  }, [scenes, daySceneIdRanges]);

  // Toggle a scene in the sides selection
  const handleToggleSceneForSides = useCallback((sceneId) => {
    setSidesSelectedSceneIds(prev => (
      prev.includes(sceneId) ? prev.filter(id => id !== sceneId) : [...prev, sceneId]
    ));
  }, []);

  // Print selected sides via hidden iframe
  const handlePrintSides = useCallback(() => {
    try {
      const selected = scenes.filter(s => sidesSelectedSceneIds.includes(s.id));
      if (!scriptRawText || selected.length === 0) {
        setSidesDialogOpen(false);
        return;
      }
      printSides({ scriptText: scriptRawText, scenes: selected, layout: sidesLayout, options: { showHeader: false } });
      setSidesDialogOpen(false);
    } catch (e) {
      console.error('Print sides failed:', e);
      setSidesDialogOpen(false);
    }
  }, [scenes, sidesSelectedSceneIds, scriptRawText, sidesLayout]);

  // Load cast members from backend
  useEffect(() => {
    const loadCastMembers = async () => {
      if (!projectId) return;
      setLoadingCast(true);
      try {
        // Get authentication token from localStorage
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No authentication token found for cast loading');
          return;
        }

        const response = await axios.get(`/api/v1/cast/project/${projectId}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-auth-token': token
          }
        });
        
        console.log('Cast members loaded:', response.data);
        setCastMembers(response.data);
      } catch (err) {
        console.error('Error loading cast members:', err);
        // Don't show an error to the user as this is an enhancement
      } finally {
        setLoadingCast(false);
      }
    };
    
    loadCastMembers();
  }, [projectId]);

  // Load scenes from backend - only if server has saved scenes
  // This effect is disabled because scenes are parsed from script content in processScript
  // The server endpoint returns empty [] which overwrites locally parsed scenes
  // Keeping this commented for reference in case server-side scene storage is implemented
  /*
  useEffect(() => {
    const loadScenes = async () => {
      if (!projectId) return;
      setLoading(true);
      try {
        const response = await axios.get(`/api/v1/scripts/project/${projectId}/scenes`);
        console.log('Scenes loaded from server:', response.data.scenes);
        
        // Only use server scenes if they exist
        const serverScenes = response.data.scenes || [];
        if (serverScenes.length === 0) {
          console.log('No scenes from server, will parse from script content');
          setLoading(false);
          return;
        }
        
        // ... rest of scene processing
        setScenes(serverScenes);
      } catch (err) {
        console.error('Error loading scenes:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadScenes();
  }, [projectId]);
  */

  const saveSceneOrder = async (newScenes) => {
    try {
      // Ensure IDs are properly set but preserve original scene numbers
      const updatedScenes = newScenes.map((scene, index) => {
        // Ensure every scene has a valid ID
        const sceneId = scene.id || `scene_${index + 1}`;
        
        // Create a clean scene object with explicit order property
        // This ensures the order is properly saved and maintained
        return {
          ...scene,
          id: sceneId, // Ensure ID is present
          // Keep the original scene number instead of recalculating
          // This ensures the scene number stays with the strip when reordered
          displayIndex: index + 1, // Add a display index for UI ordering if needed
          order: index // Explicitly set order property based on current position
        };
      });
      
      console.log('Saving scenes with explicit order:', updatedScenes.map(s => ({ id: s.id, order: s.order })));

      console.log('Updated scene order in client (preserving original scene numbers):', {
        sceneCount: updatedScenes.length,
        firstSceneId: updatedScenes[0]?.id,
        firstSceneNumber: updatedScenes[0]?.sceneNumber
      });

      // Save the scene order to localStorage for persistence
      try {
        const sceneOrderMap = {};
        updatedScenes.forEach(scene => {
          if (scene.id && scene.order !== undefined) {
            sceneOrderMap[scene.id] = scene.order;
          }
        });
        
        localStorage.setItem(`sceneOrder_${projectId}`, JSON.stringify(sceneOrderMap));
        console.log('Saved scene order to localStorage:', {
          projectId,
          sceneCount: Object.keys(sceneOrderMap).length
        });
      } catch (storageErr) {
        console.error('Error saving scene order to localStorage:', storageErr);
      }

      // Set the updated scenes in state
      setScenes(updatedScenes);
      
      // Save to backend
      const saved = await saveSceneOrderToBackend(updatedScenes);
      if (saved) {
        console.log('Successfully saved scene order to backend');
      } else {
        console.error('Failed to save scene order to backend');
      }
      
      return saved;
    } catch (err) {
      console.error('Error saving scene order:', err);
      setError('Failed to save scene order');
      return false;
    }
  };

  // Save scene order to backend
  const saveSceneOrderToBackend = async (updatedScenes) => {
    if (!projectId) {
      console.error('Cannot save scene order: No projectId provided');
      setError('Failed to save scene order: Missing project ID');
      return false;
    }

    if (!updatedScenes || !Array.isArray(updatedScenes) || updatedScenes.length === 0) {
      console.error('Cannot save scene order: Invalid scenes data', updatedScenes);
      setError('Failed to save scene order: Invalid scene data');
      return false;
    }

    try {
      console.log('Saving scene order to backend:', {
        projectId,
        scenesCount: updatedScenes.length,
        firstScene: updatedScenes[0]
      });
      
      // Get authentication token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        setError('Authentication required. Please log in again.');
        return false;
      }

      const response = await axios.put(
        `/api/v1/scripts/project/${projectId}/scenes/reorder`, 
        { scenes: updatedScenes },
        { 
          headers: { 
            'Content-Type': 'application/json',
            'x-auth-token': token 
          }
        }
      );

      console.log('Scene order save response:', {
        status: response.status,
        headers: response.headers,
        data: response.data
      });

      return response.data?.success === true;
    } catch (err) {
      console.error('Error saving scene order:', {
        error: err,
        response: err.response?.data,
        status: err.response?.status,
        projectId
      });
      
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to edit this project.');
      } else {
        setError(err.response?.data?.message || 'Failed to save scene order');
      }
      
      return false;
    }
  };

  // Load breaks for the script
  const loadBreaks = async () => {
    try {
      console.log('Loading breaks for script:', projectId);
      
      // Get authentication token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found when loading breaks');
        setError('Authentication required. Please log in again.');
        return;
      }
      
      const response = await axios.get(`/api/v1/scripts/${projectId}/breaks`, {
        headers: {
          'x-auth-token': token
        }
      });
      console.log('Loaded breaks:', response.data);
      
      // Process breaks to ensure they have scene ID references
      const processedBreaks = response.data.map(breakItem => {
        // If the break doesn't have a scene ID reference but has an index
        if (!breakItem.afterSceneId && breakItem.afterSceneIndex !== undefined) {
          // Try to find the scene at that index and get its ID
          const sceneId = scenes[breakItem.afterSceneIndex]?.id;
          if (sceneId) {
            return { ...breakItem, afterSceneId: sceneId };
          }
        }
        return breakItem;
      });
      
      // Sort breaks by afterSceneIndex
      const sortedBreaks = processedBreaks.sort((a, b) => a.afterSceneIndex - b.afterSceneIndex);
      
      // Create an indexed structure for faster lookups
      const indexedBreaks = {};
      
      // Use a Map to ensure unique breaks by ID
      const uniqueBreaksMap = new Map();
      
      // First pass: collect unique breaks
      sortedBreaks.forEach(breakItem => {
        // Use the break ID as the key to ensure uniqueness
        uniqueBreaksMap.set(breakItem._id, {
          ...breakItem,
          // Ensure afterSceneIndex is valid
          afterSceneIndex: Math.max(0, breakItem.afterSceneIndex || 0)
        });
      });
      
      // Second pass: organize breaks by scene index
      const uniqueBreaks = Array.from(uniqueBreaksMap.values());
      uniqueBreaks.forEach(breakItem => {
        const index = breakItem.afterSceneIndex;
        
        if (!indexedBreaks[index]) {
          indexedBreaks[index] = [];
        }
        indexedBreaks[index].push(breakItem);
      });
      
      console.log('Indexed breaks structure:', Object.keys(indexedBreaks).map(key => {
        return { 
          sceneIndex: key, 
          breaks: indexedBreaks[key].map(b => b._id)
        };
      }));
      
      setBreaksBySceneIndex(indexedBreaks);
      setBreaks(uniqueBreaks);
    } catch (err) {
      console.error('Error loading breaks:', {
        error: err,
        response: err.response?.data,
        status: err.response?.status,
        projectId
      });
      
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view this project.');
      } else {
        setError(err.response?.data?.message || 'Failed to load breaks');
      }
    }
  };
  
  // Reset all breaks for the script
  const resetBreaks = async () => {
    try {
      console.log('Resetting all breaks for script:', projectId);
      
      // Get authentication token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found when resetting breaks');
        setError('Authentication required. Please log in again.');
        return false;
      }
      
      // First, confirm with the user
      if (!window.confirm('Are you sure you want to remove all breaks? This action cannot be undone.')) {
        return false;
      }
      
      // Delete all breaks one by one
      const deletePromises = breaks.map(breakItem => 
        axios.delete(
          `/api/v1/scripts/${projectId}/breaks/${breakItem._id}`, 
          { 
            headers: { 
              'x-auth-token': token 
            }
          }
        )
      );
      
      // Wait for all delete operations to complete
      await Promise.all(deletePromises);
      
      // Clear breaks from state
      setBreaks([]);
      
      // Clear indexed breaks structure
      setBreaksBySceneIndex({});
      
      // Update shooting days
      setStats(prevStats => ({
        ...prevStats,
        shootingDays: 1 // Reset to 1 day since all breaks are removed
      }));
      
      console.log('Successfully reset all breaks');
      return true;
    } catch (err) {
      console.error('Error resetting breaks:', {
        error: err,
        response: err.response?.data,
        status: err.response?.status,
        projectId
      });
      
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to edit this project.');
      } else {
        setError(err.response?.data?.message || 'Failed to reset breaks');
      }
      
      return false;
    }
  };

  // Save break to backend
  const saveBreak = async (breakData) => {
    try {
      console.log('Saving break with data:', breakData);
      
      // Get authentication token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found when saving break');
        setError('Authentication required. Please log in again.');
        return null;
      }
      
      // Create request payload with custom fields
      const payload = {
        type: breakData.type,
        afterSceneIndex: breakData.afterSceneIndex,
        afterSceneId: breakData.afterSceneId, // Include the scene ID reference
        notes: breakData.notes || '',
        // Include custom fields for day breaks
        customDate: breakData.customDate || '',
        customDayNumber: breakData.customDayNumber || '',
        customPages: breakData.customPages || '',
        customTime: breakData.customTime || '',
        useCustomValues: breakData.useCustomValues || false
      };
      
      console.log('Sending request to:', `/api/v1/scripts/${projectId}/breaks`);
      console.log('Request payload:', payload);
      
      const response = await axios.post(
        `/api/v1/scripts/${projectId}/breaks`, 
        payload,
        { 
          headers: { 
            'Content-Type': 'application/json',
            'x-auth-token': token 
          }
        }
      );
      console.log('Server response:', {
        status: response.status,
        headers: response.headers,
        data: response.data
      });
      
      // Validate response
      if (!response.data || typeof response.data !== 'object') {
        console.error('Invalid response format:', response.data);
        throw new Error('Server returned invalid data format');
      }
      
      if (!response.data._id) {
        console.error('Response missing _id:', response.data);
        throw new Error('Server response missing _id field');
      }
      
      return response.data;
    } catch (err) {
      console.error('Error saving break:', {
        error: err,
        response: err.response?.data,
        status: err.response?.status,
        data: breakData
      });
      
      if (err.response) {
        console.error('Server error details:', {
          status: err.response.status,
          data: err.response.data,
          headers: err.response.headers
        });
        setError(err.response.data.message || 'Failed to save break');
      } else if (err.request) {
        console.error('No response received:', err.request);
        setError('Failed to reach the server. Please check your connection.');
      } else {
        console.error('Error:', err.message);
        setError(`Failed to save break: ${err.message}`);
      }
      return null;
    }
  };

  // Update break in backend
  const updateBreak = async (breakId, updates) => {
    if (!breakId) {
      console.error('Cannot update break: No breakId provided');
      setError('Failed to update break: Missing break ID');
      return null;
    }

    try {
      // Find the break to update to get its current afterSceneIndex
      const breakToUpdate = breaks.find(b => b._id === breakId);
      const oldSceneIndex = breakToUpdate ? breakToUpdate.afterSceneIndex : null;
      const newSceneIndex = updates.afterSceneIndex !== undefined ? updates.afterSceneIndex : oldSceneIndex;
      
      // Include projectId in the update and handle custom fields
      const payload = {
        ...updates,
        projectId: projectId,
        // Ensure custom fields are included if they exist
        customDate: updates.customDate,
        customDayNumber: updates.customDayNumber,
        customPages: updates.customPages,
        customTime: updates.customTime,
        customName: updates.customName,
        useCustomValues: updates.useCustomValues !== undefined ? updates.useCustomValues : false
      };
      
      console.log('Break update payload customDate:', updates.customDate);

      console.log('Updating break:', {
        breakId,
        updates: payload,
        updateType: typeof payload,
        updateKeys: Object.keys(payload),
        afterSceneIndex: payload.afterSceneIndex,
        afterSceneIndexType: typeof payload.afterSceneIndex,
        projectId
      });
      
      // Get authentication token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found when updating break');
        setError('Authentication required. Please log in again.');
        return null;
      }
      
      const response = await axios.put(
        `/api/v1/scripts/${projectId}/breaks/${breakId}`, 
        payload,
        { 
          headers: { 
            'Content-Type': 'application/json',
            'x-auth-token': token 
          }
        }
      );
      
      console.log('Break update response:', {
        status: response.status,
        data: response.data
      });
      
      // Update the breaks array
      setBreaks(prev => {
        const updatedBreaks = prev.map(b => 
          b._id === breakId ? { ...b, ...updates } : b
        );
        return updatedBreaks.sort((a, b) => a.afterSceneIndex - b.afterSceneIndex);
      });
      
      // Update indexed breaks structure
      if (oldSceneIndex !== null && (newSceneIndex !== oldSceneIndex || Object.keys(updates).length > 0)) {
        setBreaksBySceneIndex(prev => {
          const newIndexed = { ...prev };
          
          // Remove from old index
          if (newIndexed[oldSceneIndex]) {
            newIndexed[oldSceneIndex] = newIndexed[oldSceneIndex].filter(b => b._id !== breakId);
            if (newIndexed[oldSceneIndex].length === 0) {
              delete newIndexed[oldSceneIndex];
            }
          }
          
          // Add to new index if afterSceneIndex was updated
          if (newSceneIndex !== null) {
            if (!newIndexed[newSceneIndex]) {
              newIndexed[newSceneIndex] = [];
            } else {
              // Check if this break already exists in the target index (to prevent duplicates)
              const existingBreakIndex = newIndexed[newSceneIndex].findIndex(b => b._id === breakId);
              if (existingBreakIndex >= 0) {
                // Remove the existing break to avoid duplication
                newIndexed[newSceneIndex].splice(existingBreakIndex, 1);
              }
            }
            
            // Create updated break object
            const updatedBreak = { ...breakToUpdate, ...updates };
            newIndexed[newSceneIndex].push(updatedBreak);
          }
          
          // Log the updated structure for debugging
          console.log('Updated indexed breaks structure:', Object.keys(newIndexed).map(key => {
            return { 
              sceneIndex: key, 
              breaks: newIndexed[key].map(b => b._id)
            };
          }));
          
          return newIndexed;
        });
      }
      
      return response.data;
    } catch (err) {
      console.error('Error updating break:', {
        error: err,
        response: err.response?.data,
        status: err.response?.status,
        breakId,
        updates
      });
      
      if (err.response) {
        console.error('Server error details:', {
          status: err.response.status,
          data: err.response.data,
          headers: err.response.headers
        });
        
        if (err.response.status === 401) {
          setError('Session expired. Please log in again.');
        } else if (err.response.status === 403) {
          setError('You do not have permission to edit breaks in this project.');
        } else {
          setError(err.response.data.message || 'Failed to update break');
        }
      } else if (err.request) {
        console.error('No response received:', err.request);
        setError('Failed to reach the server. Please check your connection.');
      } else {
        console.error('Error:', err.message);
        setError(`Failed to update break: ${err.message}`);
      }
      
      return null;
    }
  };

  // Delete break from backend
  const deleteBreak = async (breakId) => {
    if (!breakId) {
      console.error('Attempted to delete break with undefined ID');
      return;
    }
    
    try {
      console.log('Deleting break:', breakId);
      
      // Get authentication token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found when deleting break');
        setError('Authentication required. Please log in again.');
        return;
      }
      
      // Find the break to delete first to get its afterSceneIndex
      const breakToDelete = breaks.find(b => b._id === breakId);
      const sceneIndex = breakToDelete ? breakToDelete.afterSceneIndex : null;
      
      const response = await axios.delete(
        `/api/v1/scripts/${projectId}/breaks/${breakId}`,
        {
          headers: {
            'x-auth-token': token
          }
        }
      );
      console.log('Break deleted:', response.data);
      
      // Update breaks array
      setBreaks(prev => prev.filter(b => b._id !== breakId));
      
      // Update indexed breaks structure
      if (sceneIndex !== null) {
        setBreaksBySceneIndex(prev => {
          const newIndexed = { ...prev };
          if (newIndexed[sceneIndex]) {
            newIndexed[sceneIndex] = newIndexed[sceneIndex].filter(b => b._id !== breakId);
            if (newIndexed[sceneIndex].length === 0) {
              delete newIndexed[sceneIndex];
            }
          }
          return newIndexed;
        });
      }
    } catch (err) {
      console.error('Error deleting break:', {
        error: err,
        response: err.response?.data,
        breakId,
        projectId
      });
      if (err.response) {
        if (err.response.status === 401) {
          setError('Session expired. Please log in again.');
        } else if (err.response.status === 403) {
          setError('You do not have permission to delete breaks in this project.');
        } else {
          setError(err.response.data.message || 'Failed to delete break');
        }
      } else {
        setError('Failed to delete break. Please check your connection.');
      }
    }
  };

  const handleBreakRemove = async (breakId) => {
    try {
      console.log('Removing break:', breakId);
      
      // Find the break to delete first to get its afterSceneIndex
      const breakToDelete = breaks.find(b => b._id === breakId);
      const sceneIndex = breakToDelete ? breakToDelete.afterSceneIndex : null;
      
      // Optimistically update state
      setBreaks(prevBreaks => {
        const updatedBreaks = prevBreaks.filter(b => b._id !== breakId);
        return updatedBreaks.sort((a, b) => a.afterSceneIndex - b.afterSceneIndex);
      });
      
      // Update indexed breaks structure
      if (sceneIndex !== null) {
        setBreaksBySceneIndex(prev => {
          const newIndexed = { ...prev };
          if (newIndexed[sceneIndex]) {
            newIndexed[sceneIndex] = newIndexed[sceneIndex].filter(b => b._id !== breakId);
            if (newIndexed[sceneIndex].length === 0) {
              delete newIndexed[sceneIndex];
            }
          }
          return newIndexed;
        });
      }

      // Delete from backend
      await axios.delete(`/api/v1/scripts/${projectId}/breaks/${breakId}`);
      console.log('Break deleted successfully:', breakId);
    } catch (err) {
      console.error('Error deleting break:', {
        error: err,
        breakId
      });
      setError('Failed to delete break');
      
      // Revert state on error
      await loadBreaks();
    }
  };

  const addBreak = async (type, afterSceneIndex) => {
    if (!projectId) {
      console.error('No projectId provided for break creation');
      setError('Cannot add break: No project ID available');
      return;
    }

    if (typeof afterSceneIndex !== 'number' || afterSceneIndex < 0) {
      console.error('Invalid afterSceneIndex:', afterSceneIndex);
      setError('Invalid scene index for break');
      return;
    }
    
    // Get the scene ID to associate with this break
    const sceneId = scenes[afterSceneIndex]?.id;
    if (!sceneId) {
      console.error('Cannot find scene ID for index:', afterSceneIndex);
      setError('Cannot add break: Scene not found');
      return;
    }

    const newBreak = {
      type,
      afterSceneIndex,
      afterSceneId: sceneId, // Store the scene ID for stable reference
      notes: '',
      projectId
    };
    
    console.log('Adding break:', newBreak);
    const savedBreak = await saveBreak(newBreak);
    if (savedBreak && savedBreak._id) {
      console.log('Break saved successfully:', savedBreak);
      
      // Update breaks array
      setBreaks(prev => {
        const newBreaks = [...prev, savedBreak];
        return newBreaks.sort((a, b) => a.afterSceneIndex - b.afterSceneIndex);
      });
      
      // Update indexed breaks structure
      setBreaksBySceneIndex(prev => {
        const newIndexed = { ...prev };
        if (!newIndexed[afterSceneIndex]) {
          newIndexed[afterSceneIndex] = [];
        }
        newIndexed[afterSceneIndex].push(savedBreak);
        return newIndexed;
      });
    } else {
      console.error('Failed to save break:', {
        newBreak,
        savedBreak,
        projectId,
        error: 'Server response missing _id'
      });
      setError('Failed to save break: Server response is invalid');
    }
  };

  const handleBreakInsert = useCallback((type, afterSceneIndex) => {
    console.log('Inserting break:', { type, afterSceneIndex });
    addBreak(type, afterSceneIndex);
  }, [addBreak]);

  const handleBreakButtonClick = async (type) => {
    let targetSceneIndex;
    
    if (selectedSceneIndex === null) {
      // If no scene is selected, show error message
      console.warn('No scene selected for break insertion');
      setError('Please select a scene first before adding a break.');
      return;
    } else {
      // Add break after the selected scene
      targetSceneIndex = selectedSceneIndex;
    }

    console.log(`Adding ${type} break after scene index ${targetSceneIndex}`);

    try {
      // Create the break data
      const breakData = {
        type: type,
        afterSceneIndex: targetSceneIndex,
        afterSceneId: scenes[targetSceneIndex]?.id,
        projectId: projectId
      };

      console.log('Creating break with data:', breakData);

      // Save the break to the backend
      const savedBreak = await saveBreak(breakData);
      
      if (savedBreak) {
        console.log('Break saved successfully:', savedBreak);
        
        // Update the breaks state
        setBreaks(prev => {
          const updatedBreaks = [...prev, savedBreak];
          return updatedBreaks.sort((a, b) => a.afterSceneIndex - b.afterSceneIndex);
        });
        
        // Update the indexed breaks structure
        setBreaksBySceneIndex(prev => {
          const updated = { ...prev };
          if (!updated[targetSceneIndex]) {
            updated[targetSceneIndex] = [];
          }
          updated[targetSceneIndex].push(savedBreak);
          return updated;
        });
        
        console.log(`Successfully added ${type} break after scene ${targetSceneIndex + 1}`);
      } else {
        console.error('Failed to save break - no data returned');
        setError('Failed to add break. Please try again.');
      }
    } catch (err) {
      console.error('Error adding break:', err);
      setError(`Failed to add ${type} break: ${err.message}`);
    }
  };

  const fetchScripts = async (selectScriptId = null) => {
    if (!projectId) return null;
      
    setLoadingScripts(true);
    try {
      const data = await getProjectScripts(projectId);
      console.log('Fetched scripts:', data); // Debug log
      
      // Filter out PDF scripts from the dropdown
      const filteredScripts = data.filter(script => {
        // Get script title and name for direct checking
        const title = (script.title || '').toLowerCase();
        const name = (script.name || '').toLowerCase();
        
        // Comprehensive check for PDF indicators
        const fileType = (script.fileType || '').toLowerCase();
        const fileName = ((script.fileName || script.name || '')).toLowerCase();
        const mimeType = ((script.mimeType || script.contentType || '')).toLowerCase();
        const extension = fileName.split('.').pop() || '';
        
        // Check all possible PDF indicators including title and name
        // Also check for specific known PDF scripts by title
        const isPdf = 
          fileType === 'pdf' || 
          extension === 'pdf' || 
          fileName.endsWith('.pdf') || 
          mimeType.includes('pdf') || 
          mimeType === 'application/pdf' ||
          title.includes('pdf') ||
          name.includes('pdf') ||
          title.includes('.pdf') ||
          name.includes('.pdf') ||
          // Add specific script title check for known PDF scripts
          title === 'boojum creek_blue revisions_9';
        
        console.log(`Script ${script.title || script.name}: isPDF = ${isPdf}`, {
          title,
          name,
          fileType,
          fileName,
          extension,
          mimeType,
          contentType: script.contentType
        });
        
        // Exclude PDFs from the list
        return !isPdf;
      });
      
      console.log('Filtered scripts (excluding PDF):', filteredScripts);
      setScripts(filteredScripts);
      
      // If a specific script ID was requested, find and select it
      if (selectScriptId) {
        const targetScript = filteredScripts.find(s => s._id === selectScriptId);
        if (targetScript) {
          console.log('[OneLiner] Selecting requested script:', targetScript._id);
          setSelectedScript(targetScript);
          return targetScript;
        }
      }
      
      // Select the most recent script, but only update state if it's a different script
      // or if no script is currently selected
      if (filteredScripts.length > 0) {
        const mostRecent = filteredScripts.reduce((prev, current) => 
          (prev.updatedAt && current.updatedAt && new Date(current.updatedAt) > new Date(prev.updatedAt)) ? current : prev
        );
        
        // Only update selectedScript if it's a different script or none is selected
        // This prevents unnecessary re-processing when tags are saved
        setSelectedScript(prev => {
          if (!prev || prev._id !== mostRecent._id) {
            console.log('[OneLiner] Selecting different script:', mostRecent._id, mostRecent.title);
            return mostRecent;
          }
          console.log('[OneLiner] Keeping current script:', prev._id);
          return prev;
        });
        return mostRecent;
      }
      
      return null;
    } catch (err) {
      console.error('Error fetching scripts:', err);
      setError('Failed to fetch scripts');
      return null;
    } finally {
      setLoadingScripts(false);
    }
  };

  // Ref to track if we're currently processing to prevent duplicate calls
  const isProcessingRef = useRef(false);
  const lastProcessedScriptIdRef = useRef(null);
  
  const processScript = async (script) => {
    // Prevent duplicate processing of the same script
    if (isProcessingRef.current && lastProcessedScriptIdRef.current === script._id) {
      console.log('[OneLiner] Skipping duplicate processScript call for:', script._id);
      return;
    }
    
    isProcessingRef.current = true;
    lastProcessedScriptIdRef.current = script._id;
    setError(null);
    setLoading(true);
    
    try {
      let content;
      
      const response = await getScriptContent(script._id, 'latest', script.projectId);
      
      console.log('Script content response:', {
        type: response.type,
        hasContent: !!response.content,
        contentLength: response.content?.length || 0
      });

      if (response.type === 'pdf') {
        content = await extractTextFromPDF(new Blob([response.content]));
      } else {
        // Handle both object response { content: '...' } and direct string response
        content = response.content || (typeof response === 'string' ? response : '');
        console.log('[OneLiner] Content extracted:', { 
          responseType: typeof response,
          hasContent: !!response.content,
          hasTags: !!(response.tags && response.tags.length),
          contentType: typeof content,
          contentLength: content?.length || 0
        });
      }

      // Save the raw text for printing sides (before preprocessing)
      setScriptRawText(content);

      if (!content || content.length === 0) {
        throw new Error('Script content is empty');
      }

      // Log the first few lines of the script to help debug montage detection
      const firstFewLines = content.split('\n').slice(0, 20);
      console.log('First few lines of script:', firstFewLines);
      
      // Check if any of the first lines contain montage
      const montageLines = firstFewLines.filter(line => 
        line.toUpperCase().includes('MONTAGE') || 
        line.toUpperCase().includes('SERIES OF SHOTS')
      );
      console.log('Potential montage lines in first 20 lines:', montageLines);

      // Normalize line endings and clean up the content
      content = content
        .replace(/\r\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        // Ensure scene headers are properly formatted
        .replace(/^(INT|EXT|INT\/EXT|I\/E)(?:\.|\/)/gim, (match) => {
          const type = match.replace(/[\.\/ ]*/g, '').toUpperCase();
          return type + '.';
        })
        // Standardize time abbreviations
        .replace(/\b(CONT|CONT\.|CONTINUOUS)\b/gi, 'CONTINUOUS')
        // Ensure montage headers are properly formatted - more lenient matching
        .replace(/^(MONTAGE|SERIES OF SHOTS|BEGIN MONTAGE|END MONTAGE)(?:[\:\-\.\s]*)/gim, (match) => {
          console.log('Preprocessing montage header:', match);
          const type = match.replace(/[\:\-\.\s]*/g, '').toUpperCase();
          return type + ':';
        })
        // Ensure scene headers start on new lines
        .replace(/([^\n])(INT\.|EXT\.|INT\/EXT\.|I\/E\.)/g, '$1\n$2')
        // Ensure montage headers start on new lines
        .replace(/([^\n])(MONTAGE:|SERIES OF SHOTS:|BEGIN MONTAGE:|END MONTAGE:)/g, '$1\n$2')
        // Clean up spaces around dashes
        .replace(/\s+-\s+/g, ' - ')
        // Normalize character names (all caps followed by dialogue)
        .replace(/^([A-Z][A-Z\s]+)$/gm, '@$1')
        // Clean up parentheticals
        .replace(/^\((.*?)\)$/gm, '($1)')
        // Left-justify description
        .replace(/^(?!\s*$)(.*)$/gm, '  $1');

      console.log('Preprocessed content sample:', content.substring(0, 200));

      // Parse the screenplay
      const scenes = [];
      const lines = content.split('\n');
      let currentScene = null;
      let firstMontageFound = false;

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // Skip empty lines
        if (!line) continue;

        // Check for scene headers - first check for traditional INT/EXT headers
        let sceneHeaderMatch = line.match(/^(INT\.|EXT\.|INT\/EXT\.|I\/E\.)(.*)/i);
        
        // Special case for first montage - be more lenient
        if (!sceneHeaderMatch && !firstMontageFound && 
            (line.toUpperCase().includes('MONTAGE') || line.toUpperCase().includes('SERIES OF SHOTS'))) {
          console.log('Special case for first montage:', line);
          sceneHeaderMatch = [line, 'MONTAGE', line.replace(/.*?(MONTAGE|SERIES OF SHOTS)/i, '').trim()];
          firstMontageFound = true;
        }
        // If not a traditional header, check for montage headers with specific formats
        else if (!sceneHeaderMatch) {
          // More lenient matching for montage headers
          sceneHeaderMatch = line.match(/^(MONTAGE|SERIES OF SHOTS|BEGIN MONTAGE|END MONTAGE)[\s\:\-\.]*(.*)/i);
          
          // Log all potential montage headers for debugging
          if (sceneHeaderMatch) {
            console.log('Potential montage header found:', {
              line,
              match: sceneHeaderMatch[0],
              prefix: sceneHeaderMatch[1],
              rest: sceneHeaderMatch[2]
            });
            
            // Only validate if there's substantial text after the header
            const afterText = sceneHeaderMatch[2].trim();
            // More lenient validation - only skip if it's clearly an action line
            if (afterText.length > 50 && afterText.includes('.') && !afterText.match(/^[A-Z0-9\s\-\/\,\.\:]+$/i)) {
              console.log('Skipping false montage header in action line:', line);
              sceneHeaderMatch = null;
            }
          }
        }
        
        if (sceneHeaderMatch) {
          if (currentScene) {
            scenes.push(currentScene);
          }
          
          // For montage scenes, use a different parsing approach
          if (sceneHeaderMatch[1].toUpperCase().includes('MONTAGE') || 
              sceneHeaderMatch[1].toUpperCase().includes('SERIES')) {
            const montageHeader = line.trim();
            console.log('Detected montage scene:', { 
              header: montageHeader,
              match: sceneHeaderMatch[0],
              prefix: sceneHeaderMatch[1],
              rest: sceneHeaderMatch[2]
            });
            
            // Extract location from the montage header if available
            let montageLocation = montageHeader
              .replace(/^(MONTAGE|SERIES OF SHOTS|BEGIN MONTAGE|END MONTAGE)[\s\-\:]*/i, '')
              .trim();
              
            // If no location specified, use a default
            if (!montageLocation || montageLocation.length < 2) {
              montageLocation = 'VARIOUS LOCATIONS';
            }
            
            console.log('Creating montage scene with location:', montageLocation);
            
            currentScene = {
              id: `scene_${scenes.length + 1}`,
              sceneNumber: { 
                standard: scenes.length + 1, 
                scriptOrder: String(scenes.length + 1) 
              },
              location_type: 'MONTAGE',
              location: montageLocation,
              area: '',
              time: 'VARIOUS',
              description: '',
              characters: new Set(),
              props: new Set(),
              vehicles: new Set(),
              elements: {
                stunts: false,
                sfx: false,
                vfx: false,
                makeup: false
              }
            };
          } else {
            const headerInfo = parseSceneHeader(line);
            currentScene = {
              id: `scene_${scenes.length + 1}`,
              sceneNumber: { 
                standard: scenes.length + 1, 
                scriptOrder: String(scenes.length + 1) 
              },
              ...headerInfo,
              description: '',
              characters: new Set(),
              props: new Set(),
              vehicles: new Set(),
              elements: {
                stunts: false,
                sfx: false,
                vfx: false,
                makeup: false
              }
            };
          }
          continue;
        }

        if (currentScene) {
          // Check if this is a character cue (all caps)
          const isCharacterCue = line.match(/^@?[A-Z][A-Z\s]+$/);
          
          if (isCharacterCue) {
            // Remove any @ prefix we might have added during preprocessing
            const characterName = line.replace(/^@/, '').trim();
            // Add the primary character (from character cue)
            const normalizedName = normalizeCharacterName(characterName, currentScene.characters);
            if (normalizedName) {
              currentScene.characters.add(normalizedName);
            }
            
            // Look ahead to get the dialogue for this character (to extract mentioned characters)
            let dialogueIndex = i + 1;
            let dialogueText = '';
            
            // Collect all following lines until we hit another character cue or scene header
            while (dialogueIndex < lines.length) {
              const dialogueLine = lines[dialogueIndex].trim();
              
              // Stop if we hit another character cue or empty line
              if (!dialogueLine || dialogueLine.match(/^@?[A-Z][A-Z\s]+$/) || 
                  dialogueLine.match(/^(INT\.|EXT\.|INT\/EXT\.|I\/E\.|MONTAGE)/i)) {
                break;
              }
              
              // Skip parentheticals (character direction)
              if (!dialogueLine.startsWith('(') || !dialogueLine.endsWith(')')) {
                dialogueText += ' ' + dialogueLine;
              }
              
              dialogueIndex++;
            }
            
            // Extract characters from dialogue by checking for capitalized names
            // This looks for ALL CAPS or Capitalized names in dialogue that might be character references
            if (dialogueText) {
              // Match ALL CAPS names (2+ characters) in dialogue
              const allCapsMatches = dialogueText.match(/\b[A-Z][A-Z]+\b/g);
              if (allCapsMatches) {
                allCapsMatches.forEach(match => {
                  // Use the normalization function to check and format the name
                  const normalizedName = normalizeCharacterName(match, currentScene.characters);
                  if (normalizedName) {
                    currentScene.characters.add(normalizedName);
                  }
                });
              }
              
              // Also look for Capitalized Words in dialogue that might be character names
              const properNamesInDialogue = dialogueText.match(/\b[A-Z][a-z]{2,}\b/g);
              if (properNamesInDialogue) {
                properNamesInDialogue.forEach(name => {
                  // Use the normalization function to check and format the name
                  const normalizedName = normalizeCharacterName(name, currentScene.characters);
                  if (normalizedName) {
                    currentScene.characters.add(normalizedName);
                  }
                });
              }
            }
          } else {
            // Not a character cue - this is description/action
            currentScene.description += line + '\n';
            
            const lowerLine = line.toLowerCase();
            if (lowerLine.includes('stunt')) currentScene.elements.stunts = true;
            if (lowerLine.includes('effect')) currentScene.elements.sfx = true;
            if (lowerLine.includes('vfx')) currentScene.elements.vfx = true;
            if (lowerLine.includes('makeup')) currentScene.elements.makeup = true;
            
            // NEW APPROACH: Focus exclusively on EXACT character name matches
            // Build a trusted character list first from the explicit cast members
            const trustedCharacters = new Set();
            
            // Add cast members to trusted list
            castMembers.forEach(member => {
              if (member.characterName && member.characterName.trim()) {
                trustedCharacters.add(member.characterName.trim().toUpperCase());
              }
            });
            
            // Add already detected characters from dialogue cues to trusted list
            currentScene.characters.forEach(character => {
              trustedCharacters.add(character.toUpperCase());
            });
            
            // For action lines, ONLY detect names that exactly match our trusted character list
            // Look for capitalized proper names in action lines
            const properNamesInAction = line.match(/\b[A-Z][a-z]{2,}\b/g);
            if (properNamesInAction && trustedCharacters.size > 0) {
              properNamesInAction.forEach(name => {
                // Check if this name exactly matches or is contained in one of our trusted characters
                const exactMatch = Array.from(trustedCharacters).some(trustedName => 
                  trustedName === name.toUpperCase() || 
                  trustedName.includes(name.toUpperCase()));
                
                if (exactMatch) {
                  // This is a trusted character name, add it
                  const normalizedName = name.charAt(0).toUpperCase() + name.slice(1);
                  currentScene.characters.add(normalizedName);
                }
                // No additional processing for non-exact matches - we're being very strict
              });
            }
            
            // Similarly, only detect ALL CAPS names if they match trusted characters
            const allCapsNamesInAction = line.match(/\b[A-Z][A-Z]+\b/g);
            if (allCapsNamesInAction && trustedCharacters.size > 0) {
              allCapsNamesInAction.forEach(name => {
                // Check if this name exactly matches one of our trusted characters
                const exactMatch = Array.from(trustedCharacters).some(trustedName => 
                  trustedName === name.toUpperCase() || 
                  trustedName.includes(name.toUpperCase()));
                
                if (exactMatch) {
                  // This is a trusted character name, add it
                  currentScene.characters.add(name);
                }
                // No additional processing for non-exact matches - we're being very strict
              });
            }
            
            // Check for props and vehicles
            const propMatches = line.match(/\b(?:with|holds?|carries?)\s+(?:a|the)\s+([A-Za-z]+)/g);
            if (propMatches) {
              propMatches.forEach(match => {
                const prop = match.replace(/(?:with|holds?|carries?)\s+(?:a|the)\s+/, '');
                currentScene.props.add(prop);
              });
            }
            
            const vehicleMatches = line.match(/\b(car|truck|van|motorcycle|bike)\b/i);
            if (vehicleMatches) {
              currentScene.vehicles.add(vehicleMatches[1].toLowerCase());
            }
          }
        }
      }

      // Add the last scene
      if (currentScene) {
        scenes.push(currentScene);
      }

      // Helper to deduplicate character names case-insensitively, keeping the first occurrence
      const dedupeCharacters = (chars) => {
        const seen = new Map(); // lowercase -> original name
        for (const char of chars) {
          const key = char.toLowerCase();
          if (!seen.has(key)) {
            // Normalize to Title Case for consistency
            const normalized = char.charAt(0).toUpperCase() + char.slice(1).toLowerCase();
            seen.set(key, normalized);
          }
        }
        return Array.from(seen.values());
      };

      // Process scenes to calculate duration and pages
      const processedScenes = scenes.map((scene, index) => ({
        ...scene,
        characters: dedupeCharacters(Array.from(scene.characters)),
        props: Array.from(scene.props),
        vehicles: Array.from(scene.vehicles),
        duration: Math.max(1, Math.ceil(scene.description.split('\n').length / 20)), // Rough estimate
        // More accurate page count calculation based on industry standards
        // Average screenplay has ~55 lines per page but we need to account for dialogue formatting
        // and other elements that take up more vertical space
        pages: Math.max(0.125, Math.ceil(scene.description.split('\n').length / 30 * 8) / 8), // In 1/8ths
        status: 'pending',
        // Add order property if it doesn't exist
        order: scene.order !== undefined ? scene.order : index
      }));

      if (processedScenes.length === 0) {
        throw new Error('No scenes found in the screenplay. Make sure the file contains proper scene headers (e.g., "INT. LOCATION - DAY")');
      }

      console.log('Successfully parsed scenes:', processedScenes.length);
      
      // Check for montage scenes in the processed result
      const montageScenes = processedScenes.filter(scene => 
        scene.location_type === 'MONTAGE' || 
        (scene.location && scene.location.toUpperCase().includes('MONTAGE'))
      );
      console.log('Montage scenes in processed result:', montageScenes);
      
      // IMPORTANT: Check if we already have scenes with order properties in state or localStorage
      // First try to get the stored scene order from localStorage
      let storedSceneOrder = null;
      try {
        const storedOrderJson = localStorage.getItem(`sceneOrder_${projectId}`);
        if (storedOrderJson) {
          storedSceneOrder = JSON.parse(storedOrderJson);
          console.log('Found stored scene order in localStorage:', {
            projectId,
            sceneCount: Object.keys(storedSceneOrder).length
          });
        }
      } catch (err) {
        console.error('Error reading stored scene order from localStorage:', err);
      }
      
      // Check if we have stored order or scenes with order properties in state
      if (storedSceneOrder && Object.keys(storedSceneOrder).length > 0) {
        console.log('Using stored scene order from localStorage');
        
        // Apply the stored orders to the processed scenes
        const orderedScenes = processedScenes.map(scene => {
          if (scene.id && storedSceneOrder[scene.id] !== undefined) {
            // Preserve the order from localStorage
            return { ...scene, order: storedSceneOrder[scene.id] };
          }
          return scene;
        });
        
        // Sort the scenes by their order property
        orderedScenes.sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          return 0;
        });
        
        console.log('Applied stored scene order:', {
          sceneCount: orderedScenes.length,
          firstSceneId: orderedScenes[0]?.id,
          firstSceneOrder: orderedScenes[0]?.order
        });
        
        // Set the ordered scenes
        setScenes(orderedScenes);
      } else if (scenes.length > 0) {
        // Create a map of scene IDs to their current order from state
        const existingSceneOrders = {};
        const currentScenes = [...scenes];
        
        // First check if the current scenes have order properties
        const hasOrderProperty = currentScenes.some(scene => scene.order !== undefined);
        
        if (hasOrderProperty) {
          console.log('Preserving existing scene order from state');
          
          // Map scene IDs to their current order
          currentScenes.forEach(scene => {
            if (scene.id && scene.order !== undefined) {
              existingSceneOrders[scene.id] = scene.order;
            }
          });
          
          // Apply the existing orders to the processed scenes
          const orderedScenes = processedScenes.map(scene => {
            if (scene.id && existingSceneOrders[scene.id] !== undefined) {
              // Preserve the order from the state
              return { ...scene, order: existingSceneOrders[scene.id] };
            }
            return scene;
          });
          
          // Sort the scenes by their order property
          orderedScenes.sort((a, b) => {
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            }
            return 0;
          });
          
          console.log('Applied existing scene order from state:', {
            sceneCount: orderedScenes.length,
            firstSceneId: orderedScenes[0]?.id,
            firstSceneOrder: orderedScenes[0]?.order
          });
          
          // Set the ordered scenes
          setScenes(orderedScenes);
        } else {
          // No existing order, just set the processed scenes
          setScenes(processedScenes);
        }
      } else {
        // No existing scenes, just set the processed scenes
        setScenes(processedScenes);
      }
    } catch (err) {
      console.error('Error processing script:', err);
      setError('Error processing script: ' + err.message);
    } finally {
      setLoading(false);
      isProcessingRef.current = false;
    }
  };

  useEffect(() => {
    // Load excluded words from localStorage (only on mount)
    setExcludedWords(getCustomExcludedWords());
  }, []);

  useEffect(() => {
    const handleScriptsUpdated = async (event) => {
      if (!projectId) return;
      const updatedProjectId = event?.detail?.projectId;
      if (!updatedProjectId || updatedProjectId !== projectId) return;
      
      const newScriptId = event?.detail?.scriptId;
      console.log('[OneLiner] scriptsUpdated event received:', { projectId, newScriptId });
      
      // Fetch updated scripts list, optionally selecting the new script
      const selectedOrNew = await fetchScripts(newScriptId);
      
      // Process the script (either newly selected or current)
      if (selectedOrNew) {
        console.log('[OneLiner] Processing script after upload:', selectedOrNew._id);
        processScript(selectedOrNew);
      } else if (selectedScript) {
        console.log('[OneLiner] Re-processing current script');
        processScript(selectedScript);
      }
    };

    window.addEventListener('scriptsUpdated', handleScriptsUpdated);
    return () => window.removeEventListener('scriptsUpdated', handleScriptsUpdated);
  }, [projectId]);

  useEffect(() => {
    if (selectedScript) {
      processScript(selectedScript);
    }
  }, [selectedScript?._id, selectedScript?.updatedAt]);

  // Single effect for initial data loading when projectId changes
  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (!projectId) return;
      
      try {
        const script = await fetchScripts();
        if (isMounted) {
          await loadBreaks();  // Load breaks after script is loaded
        }
      } catch (err) {
        console.error('Error loading data:', err);
        if (isMounted) {
          setError('Failed to load script data');
        }
      }
    };

    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [projectId]);

  const handleAddScene = () => {
    // Create a new scene object
    const newScene = {
      id: `scene_${Date.now()}`,
      sceneNumber: {
        standard: scenes.length + 1,
        scriptOrder: String(scenes.length + 1)
      },
      location_type: 'INT.',
      location: 'NEW LOCATION',
      area: '',
      time: 'DAY',
      description: '',
      characters: [],
      props: [],
      vehicles: [],
      elements: {
        stunts: false,
        sfx: false,
        vfx: false,
        makeup: false
      },
      duration: 2,
      pages: 1
    };

    // Add the new scene to the scenes array
    setScenes([...scenes, newScene]);
    
    // Select the new scene
    setSelectedSceneIndex(scenes.length);
    setSelectedScene(newScene);
    setDialogOpen(true);
    
    console.log('Added new scene:', newScene);
  };

  const handleEditScene = (scene) => {
    setSelectedScene(scene);
    setDialogOpen(true);
  };

  const handleDeleteScene = (sceneId) => {
    setScenes((prev) =>
      prev.filter((scene) => scene.id !== sceneId).map((scene, index) => ({
        ...scene,
        sceneNumber: {
          ...scene.sceneNumber,
          standard: index + 1,
          scriptOrder: String(index + 1)
        }
      }))
    );
  };

  const handleSaveScene = (updatedScene) => {
    setScenes((prev) =>
      prev.map((scene) =>
        scene.id === updatedScene.id ? updatedScene : scene
      )
    );
  };

  // Ref to the stripboard scrollable container
  const stripListRef = useRef(null);

  const SceneEditDialog = ({ open, onClose, scene, onSave }) => {
    const [editedScene, setEditedScene] = useState({ ...scene });
    const [showHiddenSceneMetadata, setShowHiddenSceneMetadata] = useState(false);
    const [contextMenu, setContextMenu] = useState(null);
    const [selectedCharacter, setSelectedCharacter] = useState(null);
    const [projectCast, setProjectCast] = useState([]);
    const [showCGScene, setShowCGScene] = useState(false);
    const [characterInput, setCharacterInput] = useState('');
    const theme = useTheme();
    const modalFieldSx = useMemo(() => ({
      '& .MuiInputLabel-root': {
        fontSize: '0.9rem',
        fontWeight: 600
      },
      '& .MuiOutlinedInput-root': {
        backdropFilter: 'blur(8px)',
        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.35 : 0.7),
        borderRadius: 2,
        transition: 'all 0.2s ease',
        '& fieldset': {
          borderWidth: 1,
          borderColor: alpha(theme.palette.divider, 0.4)
        },
        '&:hover fieldset': {
          borderColor: theme.palette.primary.main
        },
        '&.Mui-focused fieldset': {
          borderWidth: 2,
          borderColor: alpha(theme.palette.primary.main, 0.8)
        }
      }
    }), [theme]);
    const sectionCardSx = {
      p: 3,
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      background: theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.paper, 0.75)
        : alpha('#ffffff', 0.9),
      backdropFilter: 'blur(12px)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 10px 30px rgba(0,0,0,0.45)'
        : '0 14px 40px rgba(20,37,63,0.15)'
    };
    
    // Handle context menu open
    const handleContextMenu = (event, character) => {
      event.preventDefault();
      setContextMenu(
        contextMenu === null
          ? {
              mouseX: event.clientX + 2,
              mouseY: event.clientY - 6,
            }
          : null,
      );
      setSelectedCharacter(character);
    };
    
    // Handle context menu close
    const handleContextMenuClose = () => {
      setContextMenu(null);
    };
    
    // Handle marking a word as "not a character"
    const handleExcludeCharacter = () => {
      if (selectedCharacter) {
        // Add to custom excluded words list
        addToCustomExcludedWords(selectedCharacter);
        
        // Remove from current scene's characters
        const updatedChars = editedScene.characters.filter(char => char !== selectedCharacter);
        setEditedScene({ ...editedScene, characters: updatedChars });
        
        // Show feedback
        alert(`"${selectedCharacter}" will no longer be detected as a character in any scene.`);
      }
      handleContextMenuClose();
    };
    
    // Fetch cast members when dialog opens
    useEffect(() => {
      if (open && scene?.projectId) {
        fetchCastMembers(scene.projectId);
      }
    }, [open, scene]);
    
    // Fetch cast members for the project
    const fetchCastMembers = async (projectId) => {
      if (!projectId) return;
      
      setLoadingCast(true);
      try {
        const response = await axios.get(`/api/v1/cast/project/${projectId}`);
        
        console.log('Cast members loaded for scene dialog:', response.data);
        setProjectCast(response.data);
      } catch (err) {
        console.error('Error loading cast members for scene dialog:', err);
      } finally {
        setLoadingCast(false);
      }
    };
    
    // Get hierarchy color for visual indicators (matches Cast.js palette sequence)
    const getHierarchyColor = (hierarchy) => {
      const paletteCycle = [
        theme.palette.error.main,    // 1 -> red
        theme.palette.info.main,     // 2 -> blue
        theme.palette.success.main,  // 3 -> green
        theme.palette.warning.main,  // 4 -> yellow
        theme.palette.secondary?.main || theme.palette.primary.main, // 5
        theme.palette.primary.main,  // 6
        theme.palette.grey[500]      // 7
      ];
      if (!Number.isInteger(hierarchy) || hierarchy < 1) {
        const border = theme.palette.grey[400];
        return { bg: alpha(border, theme.palette.mode === 'dark' ? 0.12 : 0.08), border };
      }
      const base = paletteCycle[(hierarchy - 1) % paletteCycle.length];
      return {
        bg: alpha(base, theme.palette.mode === 'dark' ? 0.18 : 0.10),
        border: base
      };
    };

    useEffect(() => {
      setEditedScene({ ...scene });
    }, [scene]);

    const handleSave = () => {
      onSave(editedScene);
      onClose();
    };

    if (!scene) return null;

    return (
      <>
        <Dialog 
          open={open} 
          onClose={onClose}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
              bgcolor: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: 'blur(14px)',
              color: theme.palette.text.primary,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 28px 90px rgba(0,0,0,0.7)'
                : '0 28px 90px rgba(15,24,49,0.18)'
            }
          }}
        >
          <DialogTitle sx={{ p: 0 }}>
            <Box
              sx={{
                p: 3,
                background: theme.palette.mode === 'dark'
                  ? `linear-gradient(120deg, ${alpha(theme.palette.primary.main, 0.35)}, ${alpha(theme.palette.background.paper, 0.4)})`
                  : `linear-gradient(120deg, ${alpha(theme.palette.primary.light, 0.5)}, ${alpha(theme.palette.background.paper, 0.9)})`,
                borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="overline" sx={{ letterSpacing: 4, color: alpha(theme.palette.common.white, 0.8) }}>
                    SCENE {scene.sceneNumber.standard}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {buildSceneHeader ? buildSceneHeader(scene) : 'Scene Overview'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.85), maxWidth: 720 }}>
                    Refine the strip metadata, pacing, and cast breakdown for this scene. These edits feed directly into the professional stripboard and sides.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    <Chip
                      size="small"
                      label={editedScene.location_type || scene.location_type || 'I/E'}
                      sx={{ fontWeight: 600, backdropFilter: 'blur(6px)', backgroundColor: alpha(theme.palette.common.black, 0.25), color: '#fff' }}
                    />
                    <Chip
                      size="small"
                      label={(editedScene.time || scene.time || 'DAY').toUpperCase()}
                      sx={{ fontWeight: 600, backdropFilter: 'blur(6px)', backgroundColor: alpha(theme.palette.common.black, 0.25), color: '#fff' }}
                    />
                    <Chip
                      size="small"
                      label={`${formatPagesInEighths(editedScene.pages || scene.pages || 0)} pages`}
                      sx={{ fontWeight: 600, backdropFilter: 'blur(6px)', backgroundColor: alpha(theme.palette.common.black, 0.25), color: '#fff' }}
                    />
                  </Box>
                </Box>
                <IconButton
                  onClick={onClose}
                  sx={{
                    alignSelf: 'flex-start',
                    borderRadius: 2,
                    backdropFilter: 'blur(6px)',
                    backgroundColor: alpha(theme.palette.common.black, 0.2),
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.common.black, 0.35)
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              p: 3,
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.default, 0.3)
                : alpha('#f7f7fb', 0.8)
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Paper elevation={0} sx={sectionCardSx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Scene Metadata
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Slug elements that drive strip groupings and shooting breakdowns.
                    </Typography>
                  </Box>
                  <Chip
                    label={`Scene ${scene.sceneNumber.standard}`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small" sx={modalFieldSx}>
                      <InputLabel id="location-type-label">Location Type</InputLabel>
                      <Select
                        labelId="location-type-label"
                        id="location-type"
                        value={editedScene.location_type || ''}
                        label="Location Type"
                        onChange={(e) => setEditedScene({ ...editedScene, location_type: e.target.value })}
                      >
                        <MenuItem value="INT.">INT.</MenuItem>
                        <MenuItem value="EXT.">EXT.</MenuItem>
                        <MenuItem value="INT./EXT.">INT./EXT.</MenuItem>
                        <MenuItem value="I/E.">I/E.</MenuItem>
                        <MenuItem value="MONTAGE">MONTAGE</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Primary Location"
                      value={editedScene.location || ''}
                      onChange={(e) => setEditedScene({ ...editedScene, location: e.target.value })}
                      sx={modalFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Area / Set Detail"
                      value={editedScene.area || ''}
                      onChange={(e) => setEditedScene({ ...editedScene, area: e.target.value })}
                      sx={modalFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small" sx={modalFieldSx}>
                      <InputLabel id="time-label">Time of Day</InputLabel>
                      <Select
                        labelId="time-label"
                        id="time"
                        value={editedScene.time || ''}
                        label="Time of Day"
                        onChange={(e) => setEditedScene({ ...editedScene, time: e.target.value })}
                      >
                        <MenuItem value="DAY">DAY</MenuItem>
                        <MenuItem value="NIGHT">NIGHT</MenuItem>
                        <MenuItem value="MORNING">MORNING</MenuItem>
                        <MenuItem value="AFTERNOON">AFTERNOON</MenuItem>
                        <MenuItem value="EVENING">EVENING</MenuItem>
                        <MenuItem value="DAWN">DAWN</MenuItem>
                        <MenuItem value="DUSK">DUSK</MenuItem>
                        <MenuItem value="CONTINUOUS">CONTINUOUS</MenuItem>
                        <MenuItem value="CONT.">CONT.</MenuItem>
                        <MenuItem value="LATER">LATER</MenuItem>
                        <MenuItem value="SAME">SAME</MenuItem>
                        <MenuItem value="VARIOUS">VARIOUS</MenuItem>
                        <MenuItem value="VAR.">VAR.</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Duration (minutes)"
                      type="number"
                      value={editedScene.duration ?? ''}
                      onChange={(e) =>
                        setEditedScene({
                          ...editedScene,
                          duration: e.target.value ? parseInt(e.target.value, 10) : ''
                        })
                      }
                      sx={modalFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Pages (in eighths)"
                      select
                      value={editedScene.pages ?? ''}
                      onChange={(e) =>
                        setEditedScene({ ...editedScene, pages: e.target.value ? parseFloat(e.target.value) : '' })
                      }
                      sx={modalFieldSx}
                    >
                      {[0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875, 1, 1.125, 1.25, 1.375, 1.5, 1.625, 1.75, 1.875, 2].map((value) => (
                        <MenuItem key={value} value={value}>
                          {formatPagesInEighths(value)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Paper>

              <Paper elevation={0} sx={sectionCardSx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Narrative & Blocking Notes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Keep the first paragraph concise—this feeds directly into pdf sides and call sheets.
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AutoFixHighIcon />}
                    onClick={() => setShowCGScene(true)}
                  >
                    Visualize Scene
                  </Button>
                </Box>
                <TextField
                  fullWidth
                  label="Scene Description"
                  multiline
                  minRows={4}
                  value={editedScene.description || ''}
                  onChange={(e) => setEditedScene({ ...editedScene, description: e.target.value })}
                  sx={{
                    ...modalFieldSx,
                    mb: 2,
                    '& .MuiInputBase-root': {
                      fontSize: '0.95rem',
                      lineHeight: 1.6
                    }
                  }}
                />
              </Paper>

              <Paper elevation={0} sx={sectionCardSx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Cast Breakdown
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tag characters to guarantee accurate day-out-of-day and cast striping.
                    </Typography>
                  </Box>
                  <Chip
                    label={`${editedScene.characters?.length || 0} in scene`}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Characters attached to this scene
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      mb: 2,
                      minHeight: 48,
                      border: editedScene.characters?.length ? 'none' : `1px dashed ${alpha(theme.palette.text.secondary, 0.4)}`,
                      borderRadius: 2,
                      p: editedScene.characters?.length ? 0 : 2
                    }}
                  >
                    {editedScene.characters && editedScene.characters.length > 0 ? (
                      editedScene.characters.map((char, index) => {
                        const castMember = projectCast.find(
                          cast => cast.characterName && 
                          cast.characterName.toLowerCase() === char.toLowerCase()
                        );
                        
                        const chipStyle = castMember ? 
                          getHierarchyStyle(castMember.hierarchy, theme) : 
                          { bg: theme.palette.background.paper, border: theme.palette.divider };
                        
                        return (
                          <Chip
                            key={index}
                            label={char}
                            onDelete={() => {
                              const updatedChars = [...editedScene.characters];
                              updatedChars.splice(index, 1);
                              setEditedScene({ ...editedScene, characters: updatedChars });
                            }}
                            onContextMenu={(e) => handleContextMenu(e, char)}
                            sx={{ 
                              bgcolor: chipStyle.bg,
                              borderColor: chipStyle.border,
                              borderWidth: 1,
                              borderStyle: 'solid',
                              '& .MuiChip-label': {
                                fontWeight: castMember?.hierarchy <= 3 ? 'bold' : 'normal'
                              },
                              cursor: 'context-menu'
                            }}
                            avatar={
                              castMember && hasHierarchy(castMember.hierarchy) ? (
                                <Avatar 
                                  sx={{ 
                                    bgcolor: chipStyle.border,
                                    color: '#000',
                                    fontWeight: 'bold',
                                    fontSize: '12px'
                                  }}
                                >
                                  {castMember.hierarchy}
                                </Avatar>
                              ) : undefined
                            }
                          />
                        );
                      })
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No characters flagged yet. Add them below or choose from the cast list.
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    label="Add Character"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={characterInput}
                    onChange={(e) => setCharacterInput(e.target.value)}
                    placeholder="Type character name"
                    sx={modalFieldSx}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ minWidth: 120, height: 40 }}
                    onClick={() => {
                      if (characterInput.trim()) {
                        const updatedChars = [...(editedScene.characters || [])];
                        const inputLower = characterInput.trim().toLowerCase();
                        const alreadyExists = updatedChars.some(c => c.toLowerCase() === inputLower);
                        if (!alreadyExists) {
                          updatedChars.push(characterInput.trim());
                          setEditedScene({ ...editedScene, characters: updatedChars });
                          setCharacterInput('');
                        }
                      }
                    }}
                  >
                    Add
                  </Button>
                </Box>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Project cast suggestions
                </Typography>
                {loadingCast ? (
                  <CircularProgress size={24} sx={{ ml: 1 }} />
                ) : projectCast.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {projectCast.map((castMember) => {
                      if (!castMember.characterName) return null;
                      
                      const isAlreadyInScene = editedScene.characters && 
                        editedScene.characters.some(char => 
                          char.toLowerCase() === castMember.characterName.toLowerCase()
                        );
                      
                      const chipStyle = getHierarchyStyle(castMember.hierarchy, theme);
                      
                      return (
                        <Chip
                          key={castMember._id}
                          label={castMember.characterName}
                          onClick={() => {
                            if (!isAlreadyInScene) {
                              const updatedChars = [...(editedScene.characters || [])];
                              updatedChars.push(castMember.characterName);
                              setEditedScene({ ...editedScene, characters: updatedChars });
                            }
                          }}
                          disabled={isAlreadyInScene}
                          sx={{ 
                            bgcolor: chipStyle.bg,
                            borderColor: chipStyle.border,
                            borderWidth: 1,
                            borderStyle: 'solid',
                            opacity: isAlreadyInScene ? 0.6 : 1,
                            '& .MuiChip-label': {
                              fontWeight: castMember.hierarchy <= 3 ? 'bold' : 'normal'
                            }
                          }}
                          avatar={
                            hasHierarchy(castMember.hierarchy) ? (
                              <Avatar 
                                sx={{ 
                                  bgcolor: chipStyle.border,
                                  color: '#000',
                                  fontWeight: 'bold',
                                  fontSize: '12px'
                                }}
                              >
                                {castMember.hierarchy}
                              </Avatar>
                            ) : undefined
                          }
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No cast members found in this project.
                  </Typography>
                )}
              </Paper>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
              px: 3,
              py: 2,
              backdropFilter: 'blur(10px)',
              backgroundColor: alpha(theme.palette.background.paper, 0.9)
            }}
          >
            <Button onClick={onClose} startIcon={<CloseIcon />}>
              Cancel
            </Button>
            <Button onClick={handleSave} variant="contained" color="primary" startIcon={<SaveIcon />}>
              Save
            </Button>
          </DialogActions>
          
          {/* Context Menu for Character Chips */}
          <Menu
            open={contextMenu !== null}
            onClose={handleContextMenuClose}
            anchorReference="anchorPosition"
            anchorPosition={
              contextMenu !== null
                ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                : undefined
            }
          >
            <MenuItem onClick={handleExcludeCharacter}>
              Mark "{selectedCharacter}" as not a character
            </MenuItem>
          </Menu>
        </Dialog>

        <ThreeDScene open={showCGScene} onClose={() => setShowCGScene(false)} />
      </>
    );
  };

  const breakButtons = (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <ButtonGroup size="small" variant="outlined" sx={{ ml: 2 }}>
        <Tooltip title="Add Day Break">
          <IconButton 
            onClick={() => handleBreakButtonClick(BREAK_TYPES.DAY_BREAK)}
            color={selectedSceneIndex !== null ? "primary" : "default"}
            size="small"
          >
            <NightsStayIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Add Lunch Break">
          <IconButton 
            onClick={() => handleBreakButtonClick(BREAK_TYPES.LUNCH)}
            color={selectedSceneIndex !== null ? "primary" : "default"}
            size="small"
          >
            <LunchDiningIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Add Company Move">
          <IconButton 
            onClick={() => handleBreakButtonClick(BREAK_TYPES.COMPANY_MOVE)}
            color={selectedSceneIndex !== null ? "primary" : "default"}
            size="small"
          >
            <DirectionsCarIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Add Custom Break">
          <IconButton 
            onClick={() => handleBreakButtonClick(BREAK_TYPES.CUSTOM)}
            color={selectedSceneIndex !== null ? "secondary" : "default"}
            size="small"
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      </ButtonGroup>
      
      {/* Reset Breaks Button */}
      <Tooltip title="Remove all breaks">
        <span>
          <IconButton
            onClick={resetBreaks}
            color="error"
            size="small"
            sx={{ ml: 2 }}
            disabled={breaks.length === 0}
          >
            <DeleteSweepIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );

  // Handler to update shooting start date
  const handleStartDateChange = async (e) => {
    const newDate = e.target.value;
    setShootingStartDate(newDate);
    localStorage.setItem(`oneliner_startDate_${projectId}`, newDate);
    setStartDateLockState(true);
    
    // Also update Day 1 break's customDate to match the start date
    const dayBreaks = breaks.filter(b => b.type === BREAK_TYPES.DAY_BREAK)
      .sort((a, b) => a.afterSceneIndex - b.afterSceneIndex);
    
    if (dayBreaks.length > 0) {
      const day1Break = dayBreaks[0];
      console.log('Updating Day 1 break date to match start date:', newDate);
      await updateBreak(day1Break._id, { customDate: newDate });
    }
  };

  const handleUnlockStartDate = () => {
    setStartDateLockState(false);
  };

  const handleManualLockStartDate = () => {
    if (!shootingStartDate) return;
    setStartDateLockState(true);
  };

  const statsDisplay = (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CalendarTodayIcon fontSize="small" color="action" />
        <TextField
          size="small"
          type="date"
          value={shootingStartDate}
          onChange={handleStartDateChange}
          InputLabelProps={{ shrink: true }}
          sx={{ 
            width: 150,
            '& .MuiInputBase-input': { 
              py: 0.5, 
              px: 1,
              fontSize: '0.85rem'
            }
          }}
          InputProps={{
            sx: { height: 32 }
          }}
          disabled={isStartDateLocked}
        />
        <Tooltip title={isStartDateLocked ? 'Unlock to change start date' : 'Lock start date'}>
          <span>
            <IconButton
              size="small"
              onClick={isStartDateLocked ? handleUnlockStartDate : handleManualLockStartDate}
              color={isStartDateLocked ? 'primary' : 'default'}
              sx={{ ml: 0.5 }}
            >
              {isStartDateLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="body2" color="text.secondary">
          Start
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CalendarTodayIcon fontSize="small" />
        Shooting Days: <span style={{ fontWeight: 'bold' }}>{calculateShootingDays(breaks, scenes)}</span>
      </Typography>
      <Typography variant="body2">
        Pages: <span style={{ fontWeight: 'bold' }}>
          {formatPagesInEighths(scenes.reduce((sum, scene) => sum + scene.pages, 0))}
        </span>
      </Typography>
    </Box>
  );

  // Function to reorganize breaks and prevent duplication
  const reorganizeBreaks = useCallback(() => {
    // Create a Map to ensure unique breaks by ID
    const uniqueBreaksMap = new Map();
    
    // First collect all breaks to ensure we don't lose any
    breaks.forEach(breakItem => {
      uniqueBreaksMap.set(breakItem._id, {
        ...breakItem,
        // Ensure afterSceneIndex is valid
        afterSceneIndex: Math.max(0, Math.min(breakItem.afterSceneIndex || 0, scenes.length - 1))
      });
    });
    
    // Convert to array and sort by afterSceneIndex
    const uniqueBreaks = Array.from(uniqueBreaksMap.values())
      .sort((a, b) => a.afterSceneIndex - b.afterSceneIndex);
    
    // Create a new indexed structure
    const newIndexedBreaks = {};
    uniqueBreaks.forEach(breakItem => {
      const index = breakItem.afterSceneIndex;
      
      if (!newIndexedBreaks[index]) {
        newIndexedBreaks[index] = [];
      }
      newIndexedBreaks[index].push(breakItem);
    });
    
    // Update state
    setBreaks(uniqueBreaks);
    setBreaksBySceneIndex(newIndexedBreaks);
    
    console.log('Reorganized breaks structure:', Object.keys(newIndexedBreaks).map(key => {
      return { 
        sceneIndex: key, 
        breaks: newIndexedBreaks[key].map(b => b._id)
      };
    }));
  }, [breaks, scenes.length]);

  // Create a combined array of scenes and breaks for drag and drop
  const getCombinedItems = useCallback(() => {
    const items = [];
    let itemIndex = 0;
    
    // Track break IDs to prevent duplicates
    const processedBreakIds = new Set();
    
    // Process each scene and any breaks that come before it
    scenes.forEach((scene, sceneIndex) => {
      // Add any breaks that come before this scene
      const breaksBeforeScene = breaksBySceneIndex[sceneIndex - 1] || [];
      breaksBeforeScene.forEach(breakItem => {
        // Skip if we've already processed this break
        if (processedBreakIds.has(breakItem._id)) {
          return;
        }
        
        items.push({
          type: 'break',
          data: breakItem,
          id: `break-${breakItem._id}`,
          index: itemIndex++,
          sceneIndex: sceneIndex - 1
        });
        
        // Mark this break as processed
        processedBreakIds.add(breakItem._id);
      });
      
      // Add the scene
      items.push({
        type: 'scene',
        data: scene,
        id: scene.id || `scene-${sceneIndex}`,
        index: itemIndex++,
        sceneIndex
      });
    });
    
    // Add any breaks that come after the last scene
    const breaksAfterLastScene = breaksBySceneIndex[scenes.length - 1] || [];
    breaksAfterLastScene.forEach(breakItem => {
      // Skip if we've already processed this break
      if (processedBreakIds.has(breakItem._id)) {
        return;
      }
      
      items.push({
        type: 'break',
        data: breakItem,
        id: `break-${breakItem._id}`,
        index: itemIndex++,
        sceneIndex: scenes.length - 1
      });
      
      // Mark this break as processed
      processedBreakIds.add(breakItem._id);
    });
    
    return items;
  }, [scenes, breaksBySceneIndex]);

  const onDragEnd = useCallback(async (result) => {
    setIsDragging(false);
    const { source, destination, draggableId } = result;

    if (!destination) return;

    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    
    // Reorganize breaks first to ensure we're working with clean data
    reorganizeBreaks();

    console.log('Drag end:', {
      source,
      destination,
      draggableId
    });

    if (destination.droppableId === source.droppableId && destination.index === source.index) { console.log('Dropped in same location, ignoring'); return; }

    // Get the combined items
    const combinedItems = getCombinedItems();
    
    // Find the dragged item
    const draggedItem = combinedItems.find(item => item.id === draggableId);
    if (!draggedItem) { console.error('Could not find dragged item:', draggableId); return; }
    
    console.log('Dragged item:', draggedItem);
    
    // Create a new array with the item moved to the new position
    const newItems = [...combinedItems];
    newItems.splice(source.index, 1);
    newItems.splice(destination.index, 0, draggedItem);
    
    // Extract scenes and breaks from the reordered items
    const newScenes = [];
    const newBreaks = [];
    
    // Track scene indices for updating breaks
    const sceneIndexMap = {};
    
    // First pass: extract scenes and create index mapping
    newItems.forEach((item, index) => {
      if (item.type === 'scene') {
        const scene = { ...item.data };
        const originalIndex = item.sceneIndex;
        scene.order = newScenes.length;
        scene.displayIndex = newScenes.length + 1;
        newScenes.push(scene);
        sceneIndexMap[originalIndex] = newScenes.length - 1;
      }
    });
    
    // Second pass: extract breaks and update their scene references
    // Use a Set to track break IDs we've already processed to prevent duplicates
    const processedBreakIds = new Set();
    
    newItems.forEach((item, index) => {
      if (item.type === 'break') {
        const breakItem = { ...item.data };
        
        // Skip if we've already processed this break
        if (processedBreakIds.has(breakItem._id)) {
          return;
        }
        
        // Find the scene this break should be after
        let afterSceneIndex = 0;
        for (let i = index - 1; i >= 0; i--) {
          if (newItems[i].type === 'scene') {
            afterSceneIndex = newScenes.findIndex(s => s.id === newItems[i].data.id);
            break;
          }
        }
        
        // If no previous scene was found, place after the first scene
        if (afterSceneIndex < 0 && newScenes.length > 0) {
          afterSceneIndex = 0;
        }
        
        // Update the break's scene reference
        breakItem.afterSceneIndex = afterSceneIndex;
        breakItem.afterSceneId = newScenes[afterSceneIndex]?.id;
        
        // Add to processed set to prevent duplicates
        processedBreakIds.add(breakItem._id);
        
        newBreaks.push(breakItem);
      }
    });
    
    console.log('New scenes:', newScenes.map(s => ({ id: s.id, order: s.order })));
    console.log('New breaks:', newBreaks.map(b => ({ 
      id: b._id, 
      afterSceneIndex: b.afterSceneIndex,
      afterSceneId: b.afterSceneId 
    })));
    
    // Update state
    setScenes(newScenes);
    setBreaks(newBreaks);
    
    // Update the indexed breaks structure
    const newBreaksBySceneIndex = {};
    
    // Ensure each break is only added once to the indexed structure
    const uniqueBreaks = Array.from(new Map(newBreaks.map(b => [b._id, b])).values());
    
    uniqueBreaks.forEach(breakItem => {
      // Ensure the afterSceneIndex is valid
      const validIndex = Math.max(0, Math.min(breakItem.afterSceneIndex, newScenes.length - 1));
      
      if (!newBreaksBySceneIndex[validIndex]) {
        newBreaksBySceneIndex[validIndex] = [];
      }
      newBreaksBySceneIndex[validIndex].push(breakItem);
    });
    
    setBreaksBySceneIndex(newBreaksBySceneIndex);

    // Save all changes
    try {
      // Save updated scenes to backend
      const scenesSaved = await saveSceneOrder(newScenes);
      
      if (scenesSaved) {
        console.log('Successfully saved scene order to backend');
        
        // Save updated break positions
        const breakUpdatePromises = newBreaks.map(b => {
          console.log(`Saving break update: ${b._id} to position ${b.afterSceneIndex}`);
          return updateBreak(b._id, { 
            afterSceneIndex: b.afterSceneIndex,
            afterSceneId: b.afterSceneId
          });
        });
        
        if (breakUpdatePromises.length > 0) {
          await Promise.all(breakUpdatePromises);
          console.log('Successfully updated break positions after reordering');
        }
        
        // Dispatch event to notify Schedule page that scenes were reordered
        // This triggers breakdown refresh to re-associate tags with reordered scenes
        try {
          window.dispatchEvent(new CustomEvent('scenesReordered', { 
            detail: { projectId, sceneCount: newScenes.length } 
          }));
          console.log('Dispatched scenesReordered event for breakdown refresh');
        } catch (eventErr) {
          console.warn('Failed to dispatch scenesReordered event:', eventErr);
        }
      } else {
        console.error('Failed to save scene order to backend');
        setError('Failed to save scene order to server');
      }
    } catch (err) {
      console.error('Error saving scene order or updating breaks:', err);
      setError('Error saving scene order: ' + (err.response?.data?.message || err.message));
    }
  }, [getCombinedItems, reorganizeBreaks, saveSceneOrder, updateBreak]);

  // Removed addTestMontageScene function

  // Handle printing the OneLiner breakdown
  const handlePrint = () => {
    // Create a hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    
    // Get the current date for the header
    const currentDate = new Date().toLocaleDateString();
    
    // Calculate shooting schedule for print view
    const shootingSchedule = calculateShootingSchedule(scenes, breaks);
    
    // Local function to get the date for a shooting day
    const getShootDate = (dayNumber) => {
      // Start with today and add dayNumber - 1 days
      const date = new Date();
      date.setDate(date.getDate() + dayNumber - 1);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    };
    
    // Create print-friendly HTML content
    iframe.contentDocument.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Screenplay Breakdown - ${selectedScript?.title || 'Untitled'}</title>
          <style>
            body {
              font-family: 'Arial', sans-serif;
              line-height: 1.2;
              color: #000;
              margin: 0;
              padding: 20px;
              font-size: 10pt;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 10px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              page-break-inside: auto;
            }
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            th, td {
              border: 1px solid #000;
              padding: 4px 6px;
              text-align: left;
              vertical-align: top;
            }
            th {
              background-color: #f2f2f2;
              font-weight: bold;
            }
            .day-header {
              background-color: #000;
              color: #fff;
              text-align: center;
              padding: 6px;
              font-weight: bold;
              border: none;
              font-size: 11pt;
            }
            .day-break-header {
              background-color: #000000;
              color: #fff;
              text-align: center;
              padding: 4px;
              font-weight: bold;
              border: none;
              font-size: 10pt;
            }
            .lunch-break-header {
              background-color: #000000;
              color: #fff;
              text-align: center;
              padding: 4px;
              font-weight: bold;
              border: none;
              font-size: 10pt;
            }
            .company-move-header {
              background-color: #000000;
              color: #fff;
              text-align: center;
              padding: 4px;
              font-weight: bold;
              border: none;
              font-size: 10pt;
            }
            .location-header {
              background-color: #e0e0e0;
              font-weight: bold;
              text-align: center;
              padding: 5px;
              font-size: 10pt;
            }
            .scene-number {
              text-align: center;
              width: 40px;
            }
            .scene-type {
              width: 50px;
              text-align: center;
            }
            .scene-time {
              width: 60px;
              text-align: center;
            }
            .scene-description {
              min-width: 200px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 250px;
            }
            .scene-location {
              width: 150px;
            }
            .scene-cast {
              width: 120px;
            }
            .scene-pages {
              width: 60px;
              text-align: center;
            }
            .scene-notes {
              width: 60px;
              text-align: center;
            }
            .page-number {
              text-align: right;
              font-size: 8pt;
              color: #666;
              margin-top: 10px;
            }
            @media print {
              body { padding: 0; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              .no-print { display: none; }
              table { page-break-inside: auto; }
              tr { page-break-inside: avoid; }
              .day-header { background-color: #000 !important; color: #fff !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              .day-break-header { background-color: #000 !important; color: #fff !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              .lunch-break-header { background-color: #000 !important; color: #fff !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              .company-move-header { background-color: #000 !important; color: #fff !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${selectedScript?.title || 'Untitled Script'}</h1>
            <p>Shooting Breakdown - Generated on ${currentDate}</p>
          </div>
    `);
    
    // Get combined items (scenes and breaks)
    const combinedItems = getCombinedItems();
    
    // Group scenes by shooting day and location
    let currentDay = 1; // Start with day 1
    let currentLocation = '';
    let dayPageCount = 0;
    let tableOpen = false;
    let pageCounter = 1;
    
    // Start the first table
    iframe.contentDocument.write('<table>');
    iframe.contentDocument.write(`
      <thead>
        <tr>
          <th class="scene-number">Scene</th>
          <th class="scene-type">Type</th>
          <th class="scene-time">Time</th>
          <th class="scene-description">Description</th>
          <th class="scene-location">Location</th>
          <th class="scene-cast">Cast</th>
          <th class="scene-pages">Pages</th>
          <th class="scene-notes">Notes</th>
        </tr>
      </thead>
      <tbody>
    `);
    
    combinedItems.forEach(item => {
      if (item.type === 'break' && item.data.type === 'DAY_BREAK') {
        const breakItem = item.data;
        
        // Close previous day if there was one
        if (currentDay > 0) {
          // Get formatted date and call time from break item
          const displayDate = breakItem.date ? new Date(breakItem.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : getShootDate(currentDay);
          const displayTime = breakItem.callTime || estimateCallTime(dayPageCount, currentDay);
          
          iframe.contentDocument.write(`
            <tr>
              <td colspan="8" class="day-break-header" style="background-color: #000000 !important; color: #ffffff !important;">
                --- END OF DAY ${currentDay} --- ${displayDate} --- ${formatPagesInEighths(dayPageCount)} pgs. Est. Time: ${displayTime}
              </td>
            </tr>
          `);
        }
        
        // Start new day
        currentDay = breakItem.dayNumber || (currentDay + 1);
        dayPageCount = 0;
        currentLocation = '';
        
        // Get formatted date and call time from break item
        const displayDate = breakItem.date ? new Date(breakItem.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : getShootDate(currentDay);
        const displayTime = breakItem.callTime || estimateCallTime(0, currentDay);
        
        iframe.contentDocument.write(`
          <tr>
            <td colspan="8" class="day-break-header" style="background-color: #000000 !important; color: #ffffff !important;">
              DAY ${currentDay} - ${displayDate} - Call Time: ${displayTime}
            </td>
          </tr>
        `);
      } else if (item.type === 'scene') {
        const scene = item.data;
        const sceneLocation = scene.location || '';
        const sceneArea = scene.area || '';
        const fullLocation = sceneArea ? `${sceneLocation} - ${sceneArea}` : sceneLocation;
        
        // Check if location changed
        if (fullLocation !== currentLocation) {
          currentLocation = fullLocation;
          iframe.contentDocument.write(`
            <tr>
              <td colspan="8" class="location-header">
                ${fullLocation.toUpperCase()}
              </td>
            </tr>
          `);
        }
        
        // Add scene row
        const inheritedTime = getInheritedTime(scene, scenes);
        const formattedTime = formatSceneTime(inheritedTime);
        const castList = scene.characters?.map(c => c.number || c).join(', ') || '';
        const pageCount = scene.pages || 0;
        dayPageCount += pageCount;
        
        iframe.contentDocument.write(`
          <tr>
            <td class="scene-number">${scene.sceneNumber?.standard || ''}</td>
            <td class="scene-type">${scene.location_type || ''}</td>
            <td class="scene-time">${formattedTime}</td>
            <td class="scene-description">${scene.description || ''}</td>
            <td class="scene-location">${fullLocation}</td>
            <td class="scene-cast">${castList}</td>
            <td class="scene-pages">${formatPagesInEighths(pageCount)}</td>
            <td class="scene-notes">${scene.elements?.stunts ? 'Stunts' : ''}${scene.elements?.sfx ? 'SFX' : ''}${scene.elements?.vfx ? 'VFX' : ''}</td>
          </tr>
        `);
      } else if (item.type === 'break' && item.data.type !== 'DAY_BREAK') {
        const breakItem = item.data;
        let breakTitle = '';
        let breakClass = '';
        
        if (breakItem.type === 'LUNCH') {
          breakTitle = 'LUNCH BREAK';
          breakClass = 'lunch-break-header';
        } else if (breakItem.type === 'COMPANY_MOVE') {
          breakTitle = 'COMPANY MOVE';
          breakClass = 'company-move-header';
        }
        
        iframe.contentDocument.write(`
          <tr>
            <td colspan="8" class="${breakClass}" style="background-color: #000000 !important; color: #ffffff !important;">
              --- ${breakTitle} ---
            </td>
          </tr>
        `);
      }
    });
    
    // Close the last day if there was one
    if (currentDay > 0) {
      // Format the date and time for the last day
      const displayDate = getShootDate(currentDay);
      const displayTime = estimateCallTime(dayPageCount, currentDay);
      
      iframe.contentDocument.write(`
        <tr>
          <td colspan="8" class="day-break-header" style="background-color: #000000 !important; color: #ffffff !important;">
            --- END OF DAY ${currentDay} --- ${displayDate} --- ${formatPagesInEighths(dayPageCount)} pgs. Est. Time: ${displayTime}
          </td>
        </tr>
      `);
    }
    
    // Close the table
    iframe.contentDocument.write('</tbody></table>');
    
    // Add page number
    iframe.contentDocument.write(`<div class="page-number">Page ${pageCounter}</div>`);
    
    // Write to the iframe document
    iframe.contentDocument.write('</body></html>');
    iframe.contentDocument.close();
    
    // Trigger print when content is loaded
    iframe.onload = function() {
      iframe.contentWindow.print();
      
      // Remove the iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  };

  return (
    <Box sx={{ p: 3, bgcolor: theme.palette.background.default, color: theme.palette.text.primary }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={selectedScript?._id || ''}
            onChange={(e) => {
              const script = scripts.find(s => s._id === e.target.value);
              setSelectedScript(script);
            }}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) return <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}>Select script</Typography>;
              const script = scripts.find(s => s._id === selected);
              return <Typography sx={{ fontSize: '0.875rem' }}>{script?.title || script?.name || 'Untitled'}</Typography>;
            }}
            sx={{
              fontSize: '0.875rem',
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiSelect-select': { py: 0.5, px: 1 },
              bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : alpha(theme.palette.background.paper, 0.8),
              borderRadius: 1
            }}
          >
            {/* Only show non-PDF scripts in the dropdown */}
            {scripts
              .filter(script => {
                // Get script title and name for direct checking
                const title = (script.title || '').toLowerCase();
                const name = (script.name || '').toLowerCase();
                
                // Comprehensive check for PDF indicators
                const fileType = (script.fileType || '').toLowerCase();
                const fileName = ((script.fileName || script.name || '')).toLowerCase();
                const mimeType = ((script.mimeType || script.contentType || '')).toLowerCase();
                const extension = fileName.split('.').pop() || '';
                
                // Check all possible PDF indicators including title and name
                // Also check for specific known PDF scripts by title
                const isPdf = 
                  fileType === 'pdf' || 
                  extension === 'pdf' || 
                  fileName.endsWith('.pdf') || 
                  mimeType.includes('pdf') || 
                  mimeType === 'application/pdf' ||
                  title.includes('pdf') ||
                  name.includes('pdf') ||
                  title.includes('.pdf') ||
                  name.includes('.pdf') ||
                  // Add specific script title check for known PDF scripts
                  title === 'boojum creek_blue revisions_9';
                
                console.log(`Script ${script.title || script.name}: isPDF = ${isPdf}`, {
                  title,
                  name,
                  fileType,
                  fileName,
                  extension,
                  mimeType
                });
                
                return !isPdf;
              })
              .map((script) => (
                <MenuItem key={script._id} value={script._id} sx={{ fontSize: '0.875rem' }}>
                  {script.title || script.name || 'Untitled Script'}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <DragDropContext onDragEnd={onDragEnd} onDragStart={() => setIsDragging(true)}>
        {/* Toolbar - Sticky */}
        <Paper 
          elevation={0}
          sx={{ 
            mb: 2, 
            p: 2, 
            bgcolor: isDark ? theme.palette.background.paper : '#fff',
            color: theme.palette.text.primary,
            borderRadius: 0,
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 999,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 16px rgba(0,0,0,0.3)'
              : '0 2px 8px rgba(0,0,0,0.06)',
            backdropFilter: 'blur(8px)',
            width: '100%',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Scene Management - Functional Buttons */}
              <Box sx={{ display: 'flex', border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                <Tooltip title="Add New Scene">
                  <IconButton onClick={handleAddScene}>
                    <AddIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Print Breakdown">
                  <IconButton onClick={handlePrint}>
                    <PrintIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Print Sides (select scenes & layout)">
                  <span>
                    <IconButton onClick={openSidesDialog} disabled={!scriptRawText || scenes.length === 0}>
                      <ArticleIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Manage Excluded Words">
                  <IconButton onClick={() => {
                    setExcludedWords(getCustomExcludedWords());
                    setExcludedWordsDialogOpen(true);
                  }}>
                    <FilterAltOffIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* TODO: Implement these features in the future
              <Box sx={{ display: 'flex', border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                <Tooltip title="Export Stripboard">
                  <IconButton>
                    <FileDownloadIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              */}

              {/* Scene Stats */}
              {statsDisplay}
            </Box>
            
            {/* Break Buttons - Always visible when scrolling */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {breakButtons}
            </Box>
          </Box>
        </Paper>

        <Box 
        ref={stripListRef}
        sx={{ 
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1,
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          maxHeight: 'calc(100vh - 200px)',
          overflow: 'auto',
          '& .grid-cell': {
            p: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            minHeight: '48px'
          },
          '& .scene-number': { 
            width: '60px', 
            minWidth: '60px',
            maxWidth: '60px',
            justifyContent: 'center',
            boxSizing: 'border-box'
          },
          '& .location-type': { width: '80px', justifyContent: 'center' },
          '& .description': { 
            flex: 1, 
            minWidth: '300px',
            boxSizing: 'border-box',
            justifyContent: 'flex-start',
            '& .MuiTypography-root': {
              width: '100%',
              textAlign: 'left'
            }
          },
          '& .time': { 
            width: '80px', 
            minWidth: '80px',
            maxWidth: '80px',
            justifyContent: 'center',
            boxSizing: 'border-box'
          },
          '& .cast': { 
            width: '80px', 
            minWidth: '80px',
            maxWidth: '80px',
            justifyContent: 'center',
            boxSizing: 'border-box'
          },

          '& .pages': { 
            width: '60px', 
            minWidth: '60px',
            maxWidth: '60px',
            justifyContent: 'center',
            boxSizing: 'border-box'
          },
          '& .actions': { 
            width: '80px', 
            minWidth: '80px',
            maxWidth: '80px',
            justifyContent: 'center', 
            boxSizing: 'border-box',
            borderRight: 'none' 
          }
        }}>
          {/* Column Headers */}
          <Box sx={{ 
            display: 'flex',
            bgcolor: isDark ? theme.palette.background.default : '#f5f5f5',
            borderBottom: `2px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
            minWidth: 'fit-content',
            boxSizing: 'border-box',
            width: '100%'
          }}>
            <Box className="grid-cell scene-number">
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Scene</Typography>
            </Box>
            <Box className="grid-cell location-type">
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>I/E</Typography>
            </Box>
            <Box className="grid-cell description">
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Scene Description</Typography>
            </Box>
            <Box className="grid-cell time">
              <Typography variant="body2">
                D/N
              </Typography>
            </Box>
            <Box className="grid-cell cast">
              <Typography variant="body2">
                Cast
              </Typography>
            </Box>

            <Box className="grid-cell pages">
              <Typography variant="body2">
                Pages
              </Typography>
            </Box>
            <Box className="grid-cell actions">
              <Typography variant="body2">
                Actions
              </Typography>
            </Box>
          </Box>

          <Droppable droppableId="scenes-and-breaks" type="item">
            {(provided, snapshot) => {
              // Get the combined items
              const combinedItems = getCombinedItems();
              
              return (
                <Box
                  {...provided.droppableProps}
                  ref={(node) => {
                    if (provided.innerRef) provided.innerRef(node);
                  }}
                  sx={{ 
                    minWidth: 'fit-content'
                  }}
                >
                  {combinedItems.map((item, index) => {
                    if (item.type === 'break') {
                      // Render a break
                      const breakItem = item.data;
                      return (
                        <Draggable
                          key={item.id}
                          draggableId={item.id}
                          index={index}
                        >
                          {(breakDragProvided, breakSnapshot) => {
                            const breakChild = (
                              <Box
                                ref={breakDragProvided.innerRef}
                                {...breakDragProvided.draggableProps}
                                {...breakDragProvided.dragHandleProps}
                              >
                                <Break
                                  id={breakItem._id}
                                  type={breakItem.type}
                                  notes={breakItem.notes}
                                  afterSceneIndex={breakItem.afterSceneIndex}
                                  allBreaks={breaks}
                                  scenes={scenes}
                                  shootingStartDate={shootingStartDate}
                                  onRemove={() => handleBreakRemove(breakItem._id)}
                                  onUpdate={(updates) => updateBreak(breakItem._id, updates)}
                                  onUpdateBreak={updateBreak}
                                />
                              </Box>
                            );

                            if (!breakSnapshot.isDragging) {
                              return breakChild;
                            }

                            return ReactDOM.createPortal(breakChild, document.body);
                          }}
                        </Draggable>
                      );
                    } else {
                      // Render a scene
                      const scene = item.data;
                      // Use the scenes-array index provided by getCombinedItems to avoid offsets from breaks
                      const sceneIndex = item.sceneIndex;
                      
                      return (
                        <Draggable
                          key={item.id}
                          draggableId={item.id}
                          index={index}
                        >
                          {(provided, snapshot) => {
                            const sceneChild = (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                onClick={() => setSelectedSceneIndex(sceneIndex)}
                                sx={{
                                  position: 'relative',
                                  display: 'flex',
                                  bgcolor: getStripColor(scene, theme, scenes),
                                  '&:hover': {
                                    filter: 'brightness(0.98)',
                                  },
                                  cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                  borderRadius: '4px',
                                  mb: 1,
                                  overflow: 'hidden',
                                  border: selectedSceneIndex === sceneIndex ? '2px solid #1976d2' : `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#e0e0e0'}`,
                                  boxShadow: snapshot.isDragging ? '0 8px 16px rgba(0,0,0,0.15)' : 'none',
                                  opacity: snapshot.isDragging ? 0.9 : 1,
                                  zIndex: snapshot.isDragging ? 1000 : 1,
                                  transition: snapshot.isDragging ? 'none' : 'all 0.3s ease'
                                }}
                              >
                              
                              {/* Scene Number */}
                              <Box
                                sx={{
                                  width: '60px',
                                  minWidth: '60px',
                                  maxWidth: '60px',
                                  p: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'rgba(0,0,0,0.03)',
                                  boxSizing: 'border-box'
                                }}
                              >
                                <Typography variant="body2" fontWeight="bold">
                                  {scene.sceneNumber?.scriptOrder || index + 1}
                                </Typography>
                              </Box>

                              {/* I/E (Interior/Exterior) */}
                              <Box className="grid-cell location-type">
                                <Typography variant="body2">
                                  {scene.location_type || 'N/A'}
                                </Typography>
                              </Box>

                              {/* Scene Info and Description */}
                              <Box className="grid-cell description" sx={{ 
                                flexDirection: 'column', 
                                alignItems: 'flex-start'
                              }}>
                                <Typography variant="subtitle2" fontWeight="bold" align="left" sx={{ textAlign: 'left', width: '100%' }}>
                                  {scene.location_type === 'MONTAGE' ? (
                                    <span style={{ color: '#9C27B0' }}>MONTAGE: {scene.location}</span>
                                  ) : (
                                    `${scene.location || ''} ${scene.area ? '- ' + scene.area : ''}`.trim()
                                  )}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  align="left"
                                  sx={{ textAlign: 'left', width: '100%', mt: 0.5 }}
                                >
                                  {scene.description.split('\n')[0]}
                                </Typography>
                                {/* Character badges have been removed from the scene description section */}
                              </Box>
                              
                              {/* D/N (Time) */}
                              <Box className="grid-cell time">
                                <Typography variant="body2">
                                  {getInheritedTime(scene, scenes)}
                                </Typography>
                              </Box>
                              
                              {/* Cast Count & Info */}
                              <Box className="grid-cell cast">
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    flexWrap: 'wrap', 
                                    gap: 0.5, 
                                    justifyContent: 'center',
                                    mt: 0.5
                                  }}>
                                    {scene.characters && scene.characters.slice(0, 3).map((charName, idx) => {
                                      // Find matching cast member
                                      const castMember = castMembers.find(
                                        cast => cast.characterName && 
                                        cast.characterName.toLowerCase() === charName.toLowerCase()
                                      );
                                      
                                      if (castMember && hasHierarchy(castMember.hierarchy)) {
                                        const chipStyle = getHierarchyStyle(castMember.hierarchy, theme);
                                        return (
                                          <Box
                                            key={idx}
                                            sx={{
                                              width: 24,
                                              height: 24,
                                              borderRadius: '50%',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              bgcolor: chipStyle.border,
                                              color: '#000',
                                              fontSize: '10px',
                                              fontWeight: 'bold',
                                              border: '1px solid rgba(0,0,0,0.2)',
                                              // Remove duplicate title attribute
                                            }}
                                            title={`${castMember.name} as ${castMember.characterName} (#${castMember.hierarchy})`}
                                          >
                                            {castMember.hierarchy || '?'}
                                          </Box>
                                        );
                                      }
                                      // No badge if no valid hierarchy or no matching cast member
                                      return null;
                                    })}
                                    {scene.characters && scene.characters.length > 3 && (
                                      <Box
                                        sx={{
                                          width: 24,
                                          height: 24,
                                          borderRadius: '50%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          bgcolor: 'rgba(0,0,0,0.05)',
                                          color: theme.palette.text.secondary,
                                          fontSize: '10px',
                                          fontWeight: 'bold',
                                          border: '1px solid rgba(0,0,0,0.1)'
                                        }}
                                        title={`${scene.characters.length - 3} more characters`}
                                      >
                                        +{scene.characters.length - 3}
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </Box>

                              
                              {/* Pages */}
                              <Box className="grid-cell pages">
                                <Typography variant="body2">
                                  {formatPagesInEighths(scene.pages)}
                                </Typography>
                              </Box>
                              
                              {/* Actions */}
                              <Box className="grid-cell actions">
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedScene(scene);
                                    setDialogOpen(true);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                            );

                            if (!snapshot.isDragging) {
                              return sceneChild;
                            }

                            return ReactDOM.createPortal(sceneChild, document.body);
                          }}
                        </Draggable>
                      );
                    }
                  })}
                  {provided.placeholder}
                </Box>
              );
            }}
          </Droppable>
        </Box>
      </DragDropContext>

      {/* Print Sides Dialog */}
      <Dialog 
        open={sidesDialogOpen} 
        onClose={() => setSidesDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.15)}`,
            backdropFilter: 'blur(12px)',
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.9),
            boxShadow: (theme) => theme.palette.mode === 'dark' 
              ? '0 32px 90px rgba(0,0,0,0.65)'
              : '0 28px 80px rgba(26,33,75,0.18)'
          }
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box 
            sx={{
              p: { xs: 2.5, md: 3 },
              background: (theme) => theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.35)}, ${alpha(theme.palette.background.paper, 0.6)})`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.65)}, ${alpha(theme.palette.background.paper, 0.95)})`,
              borderBottom: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              position: 'relative'
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="overline" sx={{ letterSpacing: 3, color: alpha('#fff', 0.75) }}>
                SCENE SIDES
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Print Sides
              </Typography>
              <Typography variant="body2" sx={{ maxWidth: 640, color: alpha('#fff', 0.85) }}>
                Select the layout, shooting day, and exact strips to include in the PDF export shared with cast and crew.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={`${scenes.length} Scenes`}
                  sx={{ fontWeight: 600, backdropFilter: 'blur(4px)', backgroundColor: alpha('#000', 0.25), color: '#fff' }}
                />
                <Chip
                  size="small"
                  label={`${daySceneIdRanges.length || 1} Shooting Days`}
                  sx={{ fontWeight: 600, backdropFilter: 'blur(4px)', backgroundColor: alpha('#000', 0.25), color: '#fff' }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #2196F3, #1976D2)'
              }}
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Layout</Typography>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Card variant={sidesLayout === 'half-letter' ? 'elevation' : 'outlined'} sx={{ borderColor: sidesLayout === 'half-letter' ? 'primary.main' : undefined }}>
                    <CardActionArea onClick={() => setSidesLayout('half-letter')}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600}>Half-letter</Typography>
                        <Typography variant="body2" color="text.secondary">5.5 × 8.5 in</Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card variant={sidesLayout === 'two-up' ? 'elevation' : 'outlined'} sx={{ borderColor: sidesLayout === 'two-up' ? 'primary.main' : undefined }}>
                    <CardActionArea onClick={() => setSidesLayout('two-up')}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600}>2-up on Letter</Typography>
                        <Typography variant="body2" color="text.secondary">Two sides per 8.5 × 11 in</Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Day</Typography>
                {daySceneIdRanges.length > 0 ? (
                  <ButtonGroup variant="outlined" size="small">
                    {daySceneIdRanges.map((_, idx) => (
                      <Button
                        key={`day-${idx+1}`}
                        onClick={() => {
                          setSidesSelectedDay(idx + 1);
                          const ids = daySceneIdRanges[idx] || [];
                          setSidesSelectedSceneIds(ids);
                        }}
                        variant={sidesSelectedDay === idx + 1 ? 'contained' : 'outlined'}
                      >
                        Day {idx + 1}
                      </Button>
                    ))}
                  </ButtonGroup>
                ) : (
                  <Typography variant="body2" color="text.secondary">No day breaks found. Showing all scenes.</Typography>
                )}
              </Box>

              
            </Grid>

            <Grid item xs={12} md={7}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Scenes</Typography>
              <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto' }}>
                <List dense>
                  {(daySceneIdRanges[sidesSelectedDay - 1] ? scenes.filter(s => (daySceneIdRanges[sidesSelectedDay - 1] || []).includes(s.id)) : scenes).map((scene) => (
                    <ListItem key={scene.id} secondaryAction={
                      <Checkbox
                        edge="end"
                        checked={sidesSelectedSceneIds.includes(scene.id)}
                        onChange={() => handleToggleSceneForSides(scene.id)}
                      />
                    }>
                      <ListItemText
                        primaryTypographyProps={{ noWrap: true }}
                        primary={`${scene.sceneNumber?.standard ?? ''}${scene.sceneNumber?.standard ? ' — ' : ''}${buildSceneHeader(scene)}`}
                        secondary={scene.time ? String(scene.time).toUpperCase() : ''}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSidesDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePrintSides} variant="contained" disabled={!scriptRawText || sidesSelectedSceneIds.length === 0}>Print</Button>
        </DialogActions>
      </Dialog>

      <SceneEditDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedScene(null);
        }}
        scene={selectedScene}
        onSave={async (editedScene) => {
          const updatedScenes = scenes.map((s) =>
            s.id === editedScene.id ? editedScene : s
          );
          setScenes(updatedScenes);
          // Persist scene changes (including character edits) to backend
          await saveSceneOrderToBackend(updatedScenes);
        }}
      />
      
      {/* Dialog to manage excluded words */}
      <Dialog
        open={excludedWordsDialogOpen}
        onClose={() => setExcludedWordsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.15)}`,
            backdropFilter: 'blur(10px)',
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.92),
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? '0 26px 70px rgba(0,0,0,0.55)'
              : '0 20px 60px rgba(22,29,67,0.18)'
          }
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              p: { xs: 2.5, md: 3 },
              background: (theme) => theme.palette.mode === 'dark'
                ? `linear-gradient(120deg, ${alpha(theme.palette.primary.dark, 0.3)}, ${alpha(theme.palette.background.paper, 0.7)})`
                : `linear-gradient(120deg, ${alpha(theme.palette.primary.light, 0.55)}, ${alpha(theme.palette.background.paper, 0.98)})`,
              borderBottom: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              position: 'relative'
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="overline" sx={{ letterSpacing: 3, color: alpha('#fff', 0.8) }}>
                EXCLUSION LIST
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Manage Excluded Words
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#fff', 0.85) }}>
                Words in this list will never be auto-tagged as characters. Right-click a chip in the scene editor to add more.
              </Typography>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #2196F3, #1976D2)'
              }}
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <DialogContentText sx={{ mb: 2 }}>
            These words will not be detected as character names in any scene. Right-click on any character name in a scene to add it to this list.
          </DialogContentText>
          
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {excludedWords.length > 0 ? (
              excludedWords.map((word, index) => (
                <Chip
                  key={index}
                  label={word}
                  onDelete={() => {
                    removeFromCustomExcludedWords(word);
                    setExcludedWords(getCustomExcludedWords());
                    
                    // Re-process scenes to update character lists
                    if (selectedScript) {
                      processScript(selectedScript);
                    }
                  }}
                  sx={{ bgcolor: theme.palette.error.light, color: theme.palette.error.contrastText }}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No words have been excluded yet. Right-click on any character name and select "Mark as not a character" to add words to this list.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: (theme) => `1px solid ${alpha(theme.palette.divider, 0.4)}` }}>
          <Button onClick={() => setExcludedWordsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OneLiner;
